import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { adminAPI } from './services/api';
import LogoutModal from './components/LogoutModal';
import AdminDesktopSidebar from './components/AdminDesktopSidebar';
import AdminMobileSidebar from './components/AdminMobileSidebar';
import UserManagement from './components/UserManagement';
import ReportManagement from './components/ReportManagement';
import AdminWalletContent from './components/AdminWalletContent';
import FraudManagement from './components/FraudManagement';
import PayoutErrorLogs from './components/PayoutErrorLogs';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalPayouts: number;
  pendingPayouts: number;
  completedPayouts: number;
  failedPayouts: number;
  totalRevenue: number;
  todayRevenue: number;
  systemUptime: string;
  apiCallsToday: number;
  errorRate: number;
  databaseSize: string;
  serverLoad: number;
  memoryUsage: number;
  diskUsage: number;
  activeSessions: number;
  blockedUsers: number;
  systemHealth: string;
}

const AdminDashboardNew: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    completedPayouts: 0,
    failedPayouts: 0,
    totalRevenue: 2847593.50,
    todayRevenue: 125000.00,
    systemUptime: '99.9%',
    apiCallsToday: 15420,
    errorRate: 0.02,
    databaseSize: '2.4 GB',
    serverLoad: 45,
    memoryUsage: 67,
    diskUsage: 78,
    activeSessions: 1247,
    blockedUsers: 3,
    systemHealth: 'Excellent'
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [totalManagers, setTotalManagers] = useState(0);
  const [totalMerchants, setTotalMerchants] = useState(0);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [managerTotalTransactions, setManagerTotalTransactions] = useState(0);
  const [managerTotalCredit, setManagerTotalCredit] = useState(0);
  const [managerTotalDebit, setManagerTotalDebit] = useState(0);
  const [merchantTotalPayoutDebit, setMerchantTotalPayoutDebit] = useState(0);
  const [merchantTotalTransactionsDone, setMerchantTotalTransactionsDone] = useState(0);
  const [merchantTotalTransactionCharges, setMerchantTotalTransactionCharges] = useState(0);
  const [activeSettingsModal, setActiveSettingsModal] = useState<'security' | 'cron' | 'app' | null>(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [cronEnabled, setCronEnabled] = useState<boolean>(true);
  const [cronExpression, setCronExpression] = useState<string>('*/5 * * * *');
  const [cronLastSaved, setCronLastSaved] = useState<string>('');
  const [cronTriggers, setCronTriggers] = useState<Array<{name: string, url: string, expression: string}>>([]);
  const [securitySettings, setSecuritySettings] = useState({
    otp_login_enabled: true,
    require_2fa: false,
    require_otp_after_email_login: true,
    mobile_login_enabled: true,
    email_login_enabled: true,
    session_timeout_minutes: 10,
  });
  const [securityLoading, setSecurityLoading] = useState(false);
  const [appSettingsLoading, setAppSettingsLoading] = useState(false);
  const [appUploading, setAppUploading] = useState(false);
  const [appSettings, setAppSettings] = useState<{
    google_play_link: string;
    direct_apk_link: string;
    apk_versions: Array<{ file_name: string; file_url: string; uploaded_at?: string; size_bytes?: number }>;
  }>({
    google_play_link: '',
    direct_apk_link: '',
    apk_versions: []
  });
  const [activeSystemControlModal, setActiveSystemControlModal] = useState<'maintenance' | 'backup' | 'ip-security' | null>(null);
  const [ipSecurityLoading, setIpSecurityLoading] = useState(false);
  const [ipSecurity, setIpSecurity] = useState<{
    admin_whitelist_enabled: boolean;
    admin_whitelist_ips: string[];
    ip_ban_enabled: boolean;
    banned_ips: string[];
    allowed_ip_or_country_enabled: boolean;
    allowed_ip_ranges: string[];
    allowed_countries: string[];
  }>({
    admin_whitelist_enabled: false,
    admin_whitelist_ips: [],
    ip_ban_enabled: false,
    banned_ips: [],
    allowed_ip_or_country_enabled: false,
    allowed_ip_ranges: [],
    allowed_countries: []
  });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceSettings, setMaintenanceSettings] = useState({
    title: 'System Maintenance',
    description: 'We are currently performing scheduled maintenance. Please check back later.',
    enabled: false
  });

  // Backup panel state
  const [backupSettings, setBackupSettings] = useState<{ zip_password: string; backup_folder: string; last_backup: string | null }>({
    zip_password: '',
    backup_folder: './backups',
    last_backup: null
  });
  const [backupTables, setBackupTables] = useState<string[]>([]);
  const [backupSelectedTables, setBackupSelectedTables] = useState<string[]>([]);
  const [backupScope, setBackupScope] = useState<'full' | 'tables'>('full');
  const [backupList, setBackupList] = useState<{ name: string; size: number; created: string }[]>([]);
  const [backupSettingsLoading, setBackupSettingsLoading] = useState(false);
  const [backupTablesLoading, setBackupTablesLoading] = useState(false);
  const [backupListLoading, setBackupListLoading] = useState(false);
  const [backupSavingPassword, setBackupSavingPassword] = useState(false);
  const [backupCreating, setBackupCreating] = useState(false);
  const [backupPasswordInput, setBackupPasswordInput] = useState('');

  // Scheduler status state
  const [schedulerStatus, setSchedulerStatus] = useState<string>('Loading...');
  const [schedulerNextRun, setSchedulerNextRun] = useState<string>('-');
  const [schedulerTimezone, setSchedulerTimezone] = useState<string>('Asia/Kolkata');
  const [schedulerLoading, setSchedulerLoading] = useState<boolean>(false);

  // Logs modal state
  const [showLogsModal, setShowLogsModal] = useState<boolean>(false);
  const [logsData, setLogsData] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState<boolean>(false);
  const [logsPagination, setLogsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    pageSize: 25
  });
  const [logsAutoRefresh, setLogsAutoRefresh] = useState<boolean>(false);
  const [logsRefreshInterval, setLogsRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // User Activity tab state
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState<boolean>(false);
  const [activityLogsPagination, setActivityLogsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 25
  });

  // Status Checker tab state (persisted in localStorage)
  const STATUS_CHECKER_STORAGE_KEY = 'admin_status_checker_form';
  const [statusCheckerForm, setStatusCheckerForm] = useState(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STATUS_CHECKER_STORAGE_KEY) : null;
      if (raw) {
        const p = JSON.parse(raw);
        if (p && typeof p === 'object') {
          return {
            clientId: String(p.clientId ?? '').trim(),
            clientSecret: String(p.clientSecret ?? '').trim(),
            apiKey: String(p.apiKey ?? '').trim(),
            merchantRef: String(p.merchantRef ?? '').trim(),
            transactionDate: String(p.transactionDate ?? '').trim()
          };
        }
      }
    } catch (_) { /* ignore */ }
    return { clientId: '', clientSecret: '', apiKey: '', merchantRef: '', transactionDate: '' };
  });
  const [statusCheckerLoading, setStatusCheckerLoading] = useState<boolean>(false);
  const [statusCheckerLookupLoading, setStatusCheckerLookupLoading] = useState<boolean>(false);
  const [showClientSecret, setShowClientSecret] = useState<boolean>(false);
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [statusCheckerResult, setStatusCheckerResult] = useState<{
    success: boolean;
    message?: string;
    data?: { liveStatus?: string; fullResponse?: any; viyonaTransactionId?: string; bankReferenceNumber?: string; errorMessage?: string };
  } | null>(null);

  useEffect(() => {
    fetchAdminData();
    loadMaintenanceSettings(); // Load maintenance settings on component mount
  }, []);

  // Status Checker: persist form to localStorage when values change (survives page reload)
  useEffect(() => {
    try {
      localStorage.setItem(STATUS_CHECKER_STORAGE_KEY, JSON.stringify(statusCheckerForm));
    } catch (_) {
      // ignore quota errors
    }
  }, [statusCheckerForm]);

  // Debug scheduler state changes
  useEffect(() => {
    console.log('🔄 Scheduler state changed:');
    console.log('   Status:', schedulerStatus);
    console.log('   Next Run:', schedulerNextRun);
    console.log('   Timezone:', schedulerTimezone);
  }, [schedulerStatus, schedulerNextRun, schedulerTimezone]);

  // Cleanup auto-refresh interval on unmount
  useEffect(() => {
    return () => {
      if (logsRefreshInterval) {
        clearInterval(logsRefreshInterval);
      }
    };
  }, [logsRefreshInterval]);

  // Load scheduler status when CRON settings modal is opened
  useEffect(() => {
    if (activeSettingsModal === 'cron') {
      loadSchedulerStatus();
    }
  }, [activeSettingsModal]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      // Fetch admin stats from the new admin API
      const response = await adminAPI.getStats();
      const adminStats = response.data.data;

      setStats({
        totalUsers: adminStats.totalUsers || 0,
        activeUsers: adminStats.activeUsers || 0,
        totalPayouts: adminStats.totalPayouts || 0,
        pendingPayouts: adminStats.pendingPayouts || 0,
        completedPayouts: adminStats.completedPayouts || 0,
        failedPayouts: adminStats.failedPayouts || 0,
        totalRevenue: adminStats.totalRevenue || 0,
        todayRevenue: adminStats.todayRevenue || 0,
        systemUptime: adminStats.systemUptime || '99.9%',
        apiCallsToday: adminStats.apiCallsToday || 0,
        errorRate: adminStats.errorRate || 0,
        databaseSize: adminStats.databaseSize || '0 MB',
        serverLoad: adminStats.serverLoad || 0,
        memoryUsage: adminStats.memoryUsage || 0,
        diskUsage: adminStats.diskUsage || 0,
        activeSessions: adminStats.activeSessions || 0,
        blockedUsers: adminStats.blockedUsers || 0,
        systemHealth: adminStats.systemHealth || 'Good'
      });

      // Fetch dashboard overview numbers + user counts for roles
      const [overviewRes, managersRes, merchantsRes, adminsRes] = await Promise.all([
        adminAPI.getDashboardOverview(),
        adminAPI.getAllUsers({ page: 1, limit: 1, role: 'manager' }),
        adminAPI.getAllUsers({ page: 1, limit: 1, role: 'merchant' }),
        adminAPI.getAllUsers({ page: 1, limit: 1, role: 'admin' })
      ]);

      const overview = overviewRes?.data?.data;
      if (overview) {
        // Map overview values into existing placeholders used in UI (until full refactor)
        // Manager Overview
        setManagerTotalCredit(overview.manager?.totalCredit || 0);
        setManagerTotalDebit(overview.manager?.totalDebit || 0);
        setManagerTotalTransactions(overview.manager?.totalTransactions || 0);

        // Merchant Overview and Payout Management
        setStats((prev) => ({
          ...prev,
          apiCallsToday: overview.merchant?.totalPayoutDebit ?? prev.apiCallsToday,
          totalPayouts: overview.payouts?.totalPayouts ?? prev.totalPayouts,
          pendingPayouts: overview.payouts?.pendingPayouts ?? prev.pendingPayouts,
          failedPayouts: overview.payouts?.failedPayouts ?? prev.failedPayouts,
        }));
        setMerchantTotalPayoutDebit(overview.merchant?.totalPayoutDebit || 0);
        setMerchantTotalTransactionsDone(overview.merchant?.totalTransactionsDone || 0);
        setMerchantTotalTransactionCharges(overview.merchant?.totalTransactionCharges || 0);
      }

      setTotalManagers(managersRes?.data?.data?.pagination?.totalRecords || 0);
      setTotalMerchants(merchantsRes?.data?.data?.pagination?.totalRecords || 0);
      setTotalAdmins(adminsRes?.data?.data?.pagination?.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value}%`;
  };

  const adminSidebarItems = [
    { id: 'overview', label: 'System Overview', icon: '🏠' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'reports', label: 'Report Management', icon: '📊' },
    { id: 'fraud', label: 'Fraud Management', icon: '🕵️‍♂️' },
    { id: 'payout-errors', label: 'Payout Error Logs', icon: '📉' },
    { id: 'wallet', label: 'Admin Coin Wallet', icon: '💰' },
    { id: 'system', label: 'System Control', icon: '⚙️' },
    { id: 'settings', label: 'Settings', icon: '🔧' },
    { id: 'activity', label: 'User Activity', icon: '📋' },
    { id: 'status-checker', label: 'Status Checker', icon: '🔍' }
  ];


  // const adminSidebarItems = [
  //   { id: 'overview', label: 'System Overview', icon: '🏠' },
  //   { id: 'users', label: 'User Management', icon: '👥' },
  //   { id: 'system', label: 'System Control', icon: '⚙️' },
  //   { id: 'security', label: 'Security Center', icon: '🔒' },
  //   { id: 'analytics', label: 'Analytics', icon: '📊' },
  //   { id: 'logs', label: 'System Logs', icon: '📋' },
  //   { id: 'settings', label: 'Settings', icon: '🔧' },
  //   { id: 'backup', label: 'Backup & Restore', icon: '💾' },
  //   { id: 'monitoring', label: 'Monitoring', icon: '📡' }
  // ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleSystemAction = async (action: string) => {
    if (action === 'admin-wallet') {
      navigate('/admin/account_wallet');
      return;
    }
    
    try {
      const response = await adminAPI.emergencyAction(action);
      if (response.data.success) {
        console.log(`Admin action completed: ${action}`);
        alert(response.data.message);
      }
    } catch (error) {
      console.error(`Error performing action ${action}:`, error);
      alert(`Failed to perform action: ${action}`);
    }
  };

  const loadCronSettings = async () => {
    try {
      setCronLoading(true);
      const res = await adminAPI.getSystemSettings();
      const s = res?.data?.data || {};
      const cron = s.cron || {};
      setCronEnabled(cron.enabled !== false);
      setCronExpression(cron.expression || '*/5 * * * *');
      
      // Convert old format to new format if needed
      if (Array.isArray(cron.triggers)) {
        if (typeof cron.triggers[0] === 'string') {
          // Old format: array of strings
          setCronTriggers(cron.triggers.map((url: string, index: number) => ({
            name: `Trigger ${index + 1}`,
            url, 
            expression: cron.expression || '*/5 * * * *'
          })));
        } else {
          // New format: array of objects with name field
          setCronTriggers(cron.triggers.map((trigger: any, index: number) => ({
            name: trigger.name || `Trigger ${index + 1}`,
            url: trigger.url || '',
            expression: trigger.expression || '*/5 * * * *'
          })).slice(0, 10));
        }
      } else {
        setCronTriggers([]);
      }
      
      // Load scheduler status when CRON settings are loaded
      console.log('🔄 Loading scheduler status with CRON settings...');
      await loadSchedulerStatus();
    } catch (e) {
      console.error('Failed to load cron settings', e);
    } finally {
      setCronLoading(false);
    }
  };

  const loadSecuritySettings = async () => {
    try {
      setSecurityLoading(true);
      const res = await adminAPI.getSystemSettings();
      const s = res?.data?.data || {};
      const security = s.security || {};

      setSecuritySettings({
        otp_login_enabled: security.otp_login_enabled !== false,
        require_2fa: !!security.require_2fa,
        require_otp_after_email_login: security.require_otp_after_email_login !== false,
        mobile_login_enabled: security.mobile_login_enabled !== false,
        email_login_enabled: security.email_login_enabled !== false,
        session_timeout_minutes: typeof security.session_timeout_minutes === 'number'
          ? security.session_timeout_minutes
          : parseInt(String(security.session_timeout_minutes || '10'), 10) || 10,
      });
    } catch (e) {
      console.error('Failed to load security settings', e);
    } finally {
      setSecurityLoading(false);
    }
  };

  const loadAppSettings = async () => {
    try {
      setAppSettingsLoading(true);
      const res = await adminAPI.getSystemSettings();
      const s = res?.data?.data || {};
      const app = s.app_settings || {};
      setAppSettings({
        google_play_link: app.google_play_link || '',
        direct_apk_link: app.direct_apk_link || '',
        apk_versions: Array.isArray(app.apk_versions) ? app.apk_versions : []
      });
    } catch (e) {
      console.error('Failed to load app settings', e);
    } finally {
      setAppSettingsLoading(false);
    }
  };

  const saveAppSettings = async () => {
    try {
      setAppSettingsLoading(true);
      await adminAPI.updateSystemSettings({
        app_settings: {
          google_play_link: appSettings.google_play_link,
          direct_apk_link: appSettings.direct_apk_link,
          apk_versions: appSettings.apk_versions
        }
      });
      alert('App settings saved successfully');
    } catch (e: any) {
      console.error('Failed to save app settings', e);
      alert(e?.response?.data?.message || 'Failed to save app settings');
    } finally {
      setAppSettingsLoading(false);
    }
  };

  const handleUploadApk = async (file: File | null) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.apk')) {
      alert('Please select a valid .apk file');
      return;
    }
    try {
      setAppUploading(true);
      const fd = new FormData();
      fd.append('apk_file', file);
      await adminAPI.uploadAppApk(fd);
      await loadAppSettings();
      alert('APK uploaded successfully');
    } catch (e: any) {
      console.error('Failed to upload APK', e);
      const st = e?.response?.status;
      const d = e?.response?.data;
      const hint =
        st === 413
          ? 'Request too large for reverse proxy (nginx). Set client_max_body_size (e.g. 200m) for the API location.'
          : '';
      const msg =
        (d && typeof d === 'object' && (d.message || d.error)) ||
        (typeof d === 'string' ? d : null) ||
        hint ||
        e?.message ||
        'Failed to upload APK';
      alert(msg);
    } finally {
      setAppUploading(false);
    }
  };

  const loadIpSecuritySettings = async () => {
    try {
      setIpSecurityLoading(true);
      const res = await adminAPI.getSystemSettings();
      const s = res?.data?.data ?? res?.data ?? {};
      const ip = s.ip_security || {};
      const arr = (v: unknown) => (Array.isArray(v) ? v : []);
      setIpSecurity({
        admin_whitelist_enabled: !!ip.admin_whitelist_enabled,
        admin_whitelist_ips: arr(ip.admin_whitelist_ips),
        ip_ban_enabled: !!ip.ip_ban_enabled,
        banned_ips: arr(ip.banned_ips),
        allowed_ip_or_country_enabled: !!ip.allowed_ip_or_country_enabled,
        allowed_ip_ranges: arr(ip.allowed_ip_ranges),
        allowed_countries: arr(ip.allowed_countries)
      });
    } catch (e) {
      console.error('Failed to load IP security settings', e);
    } finally {
      setIpSecurityLoading(false);
    }
  };

  const saveIpSecuritySettings = async () => {
    try {
      setIpSecurityLoading(true);
      const toSave = {
        ...ipSecurity,
        admin_whitelist_ips: (ipSecurity.admin_whitelist_ips || []).filter(s => typeof s === 'string' && s.trim() !== ''),
        banned_ips: (ipSecurity.banned_ips || []).filter(s => typeof s === 'string' && s.trim() !== ''),
        allowed_ip_ranges: (ipSecurity.allowed_ip_ranges || []).filter(s => typeof s === 'string' && s.trim() !== ''),
        allowed_countries: (ipSecurity.allowed_countries || []).filter(s => typeof s === 'string' && s.trim() !== '')
      };
      await adminAPI.updateSystemSettings({ ip_security: toSave });
      alert('IP Security settings saved successfully.');
      await loadIpSecuritySettings();
    } catch (e) {
      console.error('Failed to save IP security settings', e);
      alert('Failed to save IP Security settings.');
    } finally {
      setIpSecurityLoading(false);
    }
  };

  const loadBackupData = async () => {
    try {
      setBackupSettingsLoading(true);
      setBackupTablesLoading(true);
      setBackupListLoading(true);
      const [settingsRes, tablesRes, listRes] = await Promise.all([
        adminAPI.getBackupSettings(),
        adminAPI.getBackupTables(),
        adminAPI.listBackups()
      ]);
      const settings = settingsRes?.data?.data ?? settingsRes?.data ?? {};
      setBackupSettings({
        zip_password: settings.zip_password ?? '',
        backup_folder: settings.backup_folder ?? './backups',
        last_backup: settings.last_backup ?? null
      });
      const tablesData = tablesRes?.data?.data ?? tablesRes?.data ?? {};
      const tables = Array.isArray(tablesData) ? tablesData : (tablesData?.tables ?? []);
      setBackupTables(tables);
      const listData = listRes?.data?.data ?? listRes?.data ?? {};
      const list = Array.isArray(listData) ? listData : (listData?.files ?? []);
      setBackupList(list);
    } catch (e) {
      console.error('Failed to load backup data', e);
    } finally {
      setBackupSettingsLoading(false);
      setBackupTablesLoading(false);
      setBackupListLoading(false);
    }
  };

  const saveBackupPassword = async () => {
    try {
      setBackupSavingPassword(true);
      await adminAPI.updateBackupSettings({ zip_password: backupSettings.zip_password });
      setBackupPasswordInput('');
      alert('Zip password saved. It will be used when creating backups with "Use saved password".');
    } catch (e) {
      console.error('Failed to save backup password', e);
      alert('Failed to save zip password.');
    } finally {
      setBackupSavingPassword(false);
    }
  };

  const createBackup = async () => {
    try {
      setBackupCreating(true);
      const tables = backupScope === 'full' ? undefined : backupSelectedTables.length ? backupSelectedTables : undefined;
      const useStored = !!backupSettings.zip_password && !backupPasswordInput.trim();
      const body: { tables?: string[]; password?: string; use_stored_password?: boolean } = {
        use_stored_password: useStored
      };
      if (tables && tables.length) body.tables = tables;
      if (!useStored && backupPasswordInput.trim()) body.password = backupPasswordInput.trim();
      await adminAPI.createBackup(body);
      alert('Backup created successfully. It is saved in the backup folder.');
      await loadBackupData();
    } catch (e: any) {
      console.error('Failed to create backup', e);
      alert(e?.response?.data?.message || 'Failed to create backup.');
    } finally {
      setBackupCreating(false);
    }
  };

  const downloadBackupFile = (filename: string) => {
    adminAPI.downloadBackup(filename)
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => {
        console.error('Download failed', e);
        alert('Failed to download backup file.');
      });
  };

  const addCronTrigger = () => {
    if (cronTriggers.length < 10) {
      setCronTriggers([...cronTriggers, {
        name: `Trigger ${cronTriggers.length + 1}`,
        url: '', 
        expression: cronExpression
      }]);
    }
  };

  const removeCronTrigger = (index: number) => {
    setCronTriggers(cronTriggers.filter((_, i) => i !== index));
  };

  const closeSettingsModal = () => {
    setActiveSettingsModal(null);
  };

  const openSecuritySettingsModal = async () => {
    await loadSecuritySettings();
    setActiveSettingsModal('security');
  };

  const openCronSettingsModal = async () => {
    await loadCronSettings();
    setActiveSettingsModal('cron');
  };

  const openAppSettingsModal = async () => {
    await loadAppSettings();
    setActiveSettingsModal('app');
  };

  const closeSystemControlModal = () => {
    setActiveSystemControlModal(null);
  };

  const openIpSecurityModal = async () => {
    await loadIpSecuritySettings();
    setActiveSystemControlModal('ip-security');
  };

  const openBackupModal = async () => {
    await loadBackupData();
    setActiveSystemControlModal('backup');
  };

  const openMaintenanceModal = async () => {
    await loadMaintenanceSettings();
    setActiveSystemControlModal('maintenance');
  };

  const updateCronTrigger = (index: number, field: 'name' | 'url' | 'expression', value: string) => {
    setCronTriggers(cronTriggers.map((trigger, i) => 
      i === index ? {...trigger, [field]: value} : trigger
    ));
  };

  const runCronTrigger = async (trigger: {name: string, url: string, expression: string}) => {
    if (!trigger.url || !trigger.url.trim()) {
      alert('Please enter a valid URL for this trigger');
      return;
    }

    try {
      setCronLoading(true);
      const response = await fetch(trigger.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'manual_trigger',
          cron_expression: trigger.expression,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        alert(`✅ Trigger executed successfully!\nURL: ${trigger.url}\nExpression: ${trigger.expression}`);
      } else {
        alert(`⚠️ Trigger executed but returned status ${response.status}\nURL: ${trigger.url}`);
      }
    } catch (error) {
      console.error('Error running trigger:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`❌ Failed to execute trigger\nURL: ${trigger.url}\nError: ${errorMessage}`);
    } finally {
      setCronLoading(false);
    }
  };

  const runAllCronTriggers = async () => {
    const validTriggers = cronTriggers.filter(t => t.url && t.url.trim());
    
    if (validTriggers.length === 0) {
      alert('No valid triggers to run. Please add at least one trigger with a valid URL.');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Are you sure you want to run all ${validTriggers.length} triggers?`)) {
      return;
    }

    try {
      setCronLoading(true);
      const results: Array<{url: string, expression: string, success: boolean, status?: number, error?: string}> = [];
      
      for (const trigger of validTriggers) {
        try {
          const response = await fetch(trigger.url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'manual_trigger',
              cron_expression: trigger.expression,
              timestamp: new Date().toISOString()
            })
          });
          
          results.push({
            url: trigger.url,
            expression: trigger.expression,
            success: response.ok,
            status: response.status
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          results.push({
            url: trigger.url,
            expression: trigger.expression,
            success: false,
            error: errorMessage
          });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      
      alert(`🎯 Batch execution completed!\n✅ Successful: ${successCount}\n❌ Failed: ${failCount}\n\nCheck console for detailed results.`);
      
      console.log('Batch trigger results:', results);
    } catch (error) {
      console.error('Error running batch triggers:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`❌ Failed to run batch triggers: ${errorMessage}`);
    } finally {
      setCronLoading(false);
    }
  };

  const saveSecuritySettings = async () => {
    try {
      setSecurityLoading(true);
      const payload = {
        security: securitySettings
      };

      const response = await adminAPI.updateSystemSettings(payload);
      console.log('✅ Security settings saved:', response.data);
      alert('Security settings saved successfully');
    } catch (e: any) {
      console.error('❌ Failed to save security settings:', e);
      alert(e.response?.data?.message || 'Failed to save security settings');
    } finally {
      setSecurityLoading(false);
    }
  };

  const saveCronSettings = async () => {
    try {
      setCronLoading(true);
      await adminAPI.updateSystemSettings({
        cron: {
          enabled: cronEnabled,
          expression: cronExpression,
          triggers: cronTriggers.filter((t) => t.url && t.url.trim()).slice(0, 10)
        }
      });
      setCronLastSaved(new Date().toLocaleString());
      alert('CRON settings saved');
    } catch (e) {
      console.error('Failed to save cron settings', e);
      alert('Failed to save CRON settings');
    } finally {
      setCronLoading(false);
    }
  };

  const loadMaintenanceSettings = async () => {
    try {
      setMaintenanceLoading(true);
      console.log('📥 [LOAD SETTINGS] Starting to load maintenance settings...');
      
      const res = await adminAPI.getSystemSettings();
      console.log('📥 [LOAD SETTINGS] Raw API response:', res?.data);
      
      const settings = res?.data?.data || {};
      const maintenance = settings.maintainance || {};
      
      console.log('📥 [LOAD SETTINGS] Extracted maintenance settings:', maintenance);
      console.log('📥 [LOAD SETTINGS] Maintenance enabled:', maintenance.enabled);
      
      const newSettings = {
        title: maintenance.title || 'System Maintenance',
        description: maintenance.description || 'We are currently performing scheduled maintenance. Please check back later.',
        enabled: maintenance.enabled || false
      };
      
      console.log('📥 [LOAD SETTINGS] Final settings to set:', newSettings);
      console.log('📥 [LOAD SETTINGS] Will show in UI - Enabled:', newSettings.enabled);
      setMaintenanceSettings(newSettings);
      console.log('📥 [LOAD SETTINGS] Settings state updated successfully');
    } catch (e: any) {
      console.error('❌ [LOAD SETTINGS] Failed to load maintenance settings:', e);
      console.error('❌ [LOAD SETTINGS] Error details:', e.response?.data || e.message);
      console.error('❌ [LOAD SETTINGS] Full error:', e);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const saveMaintenanceSettings = async () => {
    try {
      setMaintenanceLoading(true);
      console.log('💾 Saving maintenance settings:', maintenanceSettings);
      
      const response = await adminAPI.updateSystemSettings({
        maintainance: maintenanceSettings
      });
      
      console.log('✅ Save response:', response.data);
      alert('Maintenance settings saved successfully');
    } catch (e: any) {
      console.error('❌ Failed to save maintenance settings:', e);
      console.error('Error details:', e.response?.data || e.message);
      alert(`Failed to save maintenance settings: ${e.response?.data?.message || e.message}`);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      setMaintenanceLoading(true);
      const newEnabled = !maintenanceSettings.enabled;
      
      console.log('🔄 [START] Toggling maintenance mode');
      console.log('   Current state:', maintenanceSettings.enabled);
      console.log('   New state:', newEnabled);
      console.log('   Full settings:', maintenanceSettings);
      
      const payload = {
        maintainance: {
          title: maintenanceSettings.title,
          description: maintenanceSettings.description,
          enabled: newEnabled
        }
      };
      
      console.log('   Sending payload:', payload);
      
      const response = await adminAPI.updateSystemSettings(payload);
      console.log('   API Response:', response.data);
      
      // Reload settings from server to ensure consistency
      console.log('   Reloading settings from server...');
      await loadMaintenanceSettings();
      
      console.log('✅ [COMPLETE] Maintenance mode toggled successfully');
      alert(`Maintenance mode ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (e: any) {
      console.error('❌ Failed to toggle maintenance mode', e);
      console.error('   Error details:', e.response?.data);
      alert('Failed to toggle maintenance mode');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  // Scheduler functions
  const loadSchedulerStatus = async () => {
    try {
      console.log('🔄 Loading scheduler status...');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
      const url = `${API_BASE_URL}/cron/scheduler-status`;
      console.log('🌐 API URL:', url);
      
      const response = await fetch(url);
      console.log('📡 Response status:', response.status);
      
      const data = await response.json();
      console.log('📊 Raw API Response:', data);
      
      if (data.success) {
        console.log('✅ Setting scheduler state:');
        console.log('   Status:', data.data.status);
        console.log('   Next Run:', data.data.nextRun);
        console.log('   Timezone:', data.data.timezone);
        
        setSchedulerStatus(data.data.status);
        setSchedulerNextRun(data.data.nextRun || 'Not scheduled');
        setSchedulerTimezone(data.data.timezone || 'Asia/Kolkata');
        
        console.log('✅ Scheduler state updated successfully');
        console.log('🔍 Current state values:');
        console.log('   schedulerStatus:', data.data.status);
        console.log('   schedulerNextRun:', data.data.nextRun || 'Not scheduled');
        console.log('   schedulerTimezone:', data.data.timezone || 'Asia/Kolkata');
      } else {
        console.error('❌ API returned error:', data);
        setSchedulerStatus('Error');
        setSchedulerNextRun('Error loading status');
        setSchedulerTimezone('Asia/Kolkata');
      }
    } catch (error) {
      console.error('❌ Failed to load scheduler status:', error);
      setSchedulerStatus('Error');
      setSchedulerNextRun('Connection error');
      setSchedulerTimezone('Asia/Kolkata');
    }
  };

  const startScheduler = async () => {
    try {
      setSchedulerLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
      const response = await fetch(`${API_BASE_URL}/cron/start-scheduler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(data.message);
        await loadSchedulerStatus();
      } else {
        alert('Failed to start scheduler: ' + data.message);
      }
    } catch (error) {
      console.error('Error starting scheduler:', error);
      alert('Error starting scheduler: ' + error);
    } finally {
      setSchedulerLoading(false);
    }
  };

  const stopScheduler = async () => {
    try {
      setSchedulerLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
      const response = await fetch(`${API_BASE_URL}/cron/stop-scheduler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(data.message);
        await loadSchedulerStatus();
      } else {
        alert('Failed to stop scheduler: ' + data.message);
      }
    } catch (error) {
      console.error('Error stopping scheduler:', error);
      alert('Error stopping scheduler: ' + error);
    } finally {
      setSchedulerLoading(false);
    }
  };

  const refreshSchedulerStatus = async () => {
    console.log('🔄 Manual refresh triggered');
    await loadSchedulerStatus();
  };

  // Test function to manually set scheduler values
  const testSchedulerValues = () => {
    console.log('🧪 Testing scheduler values manually');
    setSchedulerStatus('running');
    setSchedulerNextRun('Test Next Run');
    setSchedulerTimezone('Test Timezone');
  };

  // Logs modal functions
  const openLogsModal = async () => {
    setShowLogsModal(true);
    await fetchLogsData();
    // Start auto-refresh when modal opens
    startAutoRefresh();
  };

  const closeLogsModal = () => {
    setShowLogsModal(false);
    setLogsData([]);
    setLogsPagination({
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      pageSize: 25
    });
    // Stop auto-refresh when modal closes
    stopAutoRefresh();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= logsPagination.totalPages) {
      fetchLogsData(page);
    }
  };

  const handleRefreshLogs = () => {
    fetchLogsData(logsPagination.currentPage);
  };

  const startAutoRefresh = () => {
    if (logsRefreshInterval) {
      clearInterval(logsRefreshInterval);
    }
    
    const interval = setInterval(() => {
      if (showLogsModal && logsAutoRefresh) {
        console.log('🔄 Auto-refreshing logs...');
        fetchLogsData(logsPagination.currentPage);
      }
    }, 30000); // 30 seconds
    
    setLogsRefreshInterval(interval);
    setLogsAutoRefresh(true);
  };

  const stopAutoRefresh = () => {
    if (logsRefreshInterval) {
      clearInterval(logsRefreshInterval);
      setLogsRefreshInterval(null);
    }
    setLogsAutoRefresh(false);
  };

  const toggleAutoRefresh = () => {
    if (logsAutoRefresh) {
      stopAutoRefresh();
    } else {
      startAutoRefresh();
    }
  };

  const fetchLogsData = async (page: number = 1) => {
    try {
      setLogsLoading(true);
      console.log('🔄 Fetching CRON logs...', `Page: ${page}`);
      
      // Use adminAPI service with pagination parameters
      const response = await adminAPI.getCronLogs({
        page: page,
        limit: logsPagination.pageSize
      });
      const data = response.data;
      
      if (data.success) {
        setLogsData(data.data.logs || []);
        setLogsPagination({
          currentPage: data.data.pagination?.currentPage || page,
          totalPages: data.data.pagination?.totalPages || 1,
          totalRecords: data.data.pagination?.totalRecords || 0,
          pageSize: logsPagination.pageSize
        });
        console.log('📊 Logs loaded:', data.data.logs?.length || 0, 'entries');
        console.log('📊 Pagination:', data.data.pagination);
      } else {
        console.error('❌ Failed to load logs:', data.message);
        setLogsData([]);
      }
    } catch (error) {
      console.error('❌ Error fetching logs:', error);
      setLogsData([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadActivityLogs = async (page: number = 1) => {
    try {
      setActivityLogsLoading(true);
      const response = await adminAPI.getActivityLogs({
        page,
        limit: activityLogsPagination.limit
      });
      const data = response.data;
      if (data.success) {
        setActivityLogs(data.data.activities || []);
        setActivityLogsPagination(prev => ({
          ...prev,
          currentPage: data.data.pagination?.currentPage ?? page,
          totalPages: data.data.pagination?.totalPages ?? 1,
          totalRecords: data.data.pagination?.totalRecords ?? 0
        }));
      } else {
        setActivityLogs([]);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      setActivityLogs([]);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const runStatusChecker = async () => {
    const { clientId, clientSecret, apiKey, merchantRef, transactionDate } = statusCheckerForm;
    if (!clientId.trim() || !clientSecret.trim() || !apiKey.trim() || !merchantRef.trim() || !transactionDate.trim()) {
      setStatusCheckerResult({ success: false, message: 'Please fill all fields (Client ID, Client Secret, Api Key, Merchant Reference Number, Transaction Date).' });
      return;
    }
    setStatusCheckerResult(null);
    setStatusCheckerLoading(true);
    try {
      const res = await adminAPI.checkPayoutStatusWithCredentials({
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
        api_key: apiKey.trim(),
        merchant_reference_number: merchantRef.trim(),
        transaction_date: transactionDate.trim()
      });
      const data = res.data;
      setStatusCheckerResult({
        success: data.success,
        message: data.message ?? undefined,
        data: data.data
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Request failed';
      const data = err?.response?.data?.data;
      setStatusCheckerResult({
        success: false,
        message: msg,
        data: data ? { fullResponse: data } : undefined
      });
    } finally {
      setStatusCheckerLoading(false);
    }
  };

  const fetchStatusCheckerCredentials = async () => {
    const ref = statusCheckerForm.merchantRef.trim();
    if (!ref) return;
    setStatusCheckerLookupLoading(true);
    setStatusCheckerResult(null);
    try {
      const res = await adminAPI.getPayoutCredentialsForStatusChecker(ref);
      const d = res.data?.data;
      if (d?.client_id != null || d?.client_secret != null || d?.api_key != null || d?.transaction_date != null) {
        setStatusCheckerForm(prev => ({
          ...prev,
          clientId: d.client_id ?? prev.clientId,
          clientSecret: d.client_secret ?? prev.clientSecret,
          apiKey: d.api_key ?? prev.apiKey,
          transactionDate: d.transaction_date ?? prev.transactionDate
        }));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Lookup failed';
      setStatusCheckerResult({ success: false, message: msg });
    } finally {
      setStatusCheckerLookupLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'activity') {
      loadActivityLogs(activityLogsPagination.currentPage);
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading Admin Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-new">
      {/* Mobile Top Bar */}
      <div className="admin-mobile-topbar">
        <button
          className="admin-mobile-menu-toggle"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          aria-label="Open navigation"
        >
          ☰
        </button>
        <span className="admin-mobile-topbar-title">Admin Panel</span>
      </div>

      {/* Desktop Sidebar - Only visible on desktop */}
      <AdminDesktopSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        sidebarItems={adminSidebarItems}
      />

      {/* Mobile Sidebar - Only visible on mobile */}
      <AdminMobileSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        sidebarItems={adminSidebarItems}
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      {/* Main Admin Content */}
      <div className="admin-main-content">
        {/* Admin Header */}
        <div className="admin-header">
          <div className="admin-header-left">
            <h1>Admin Control Center</h1>
            <p>Welcome back, {user?.name} - System Administrator</p>
          </div>
          <div className="admin-header-right">
            <button className="admin-btn-secondary" onClick={handleLogoutClick}>
              <span className="btn-icon">🚪</span>
              Logout
            </button>
          </div>
        </div>

        {/* Conditional Content Rendering */}
        {activeTab === 'overview' && (
          <div className="admin-dashboard-content">

            {/* Quick Stats Banner */}
            <div className="quick-stats-banner">
              <div className="quick-stat-item">
                <div className="quick-stat-icon">🎯</div>
                <div className="quick-stat-info">
                  <div className="quick-stat-label">System Status</div>
                  <div className="quick-stat-value status-excellent">{stats.systemHealth}</div>
                </div>
              </div>
              <div className="quick-stat-item">
                <div className="quick-stat-icon">⏱️</div>
                <div className="quick-stat-info">
                  <div className="quick-stat-label">Uptime</div>
                  <div className="quick-stat-value">{stats.systemUptime}</div>
                </div>
              </div>
              <div className="quick-stat-item">
                <div className="quick-stat-icon">📡</div>
                <div className="quick-stat-info">
                  <div className="quick-stat-label">API Calls Today</div>
                  <div className="quick-stat-value">{stats.apiCallsToday.toLocaleString()}</div>
                </div>
              </div>
              <div className="quick-stat-item">
                <div className="quick-stat-icon">🗄️</div>
                <div className="quick-stat-info">
                  <div className="quick-stat-label">Database Size</div>
                  <div className="quick-stat-value">{stats.databaseSize}</div>
                </div>
              </div>
            </div>

            {/* System Health Section */}
            <div className="admin-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>⚡ System Performance</h2>
                  <span className="section-badge live-badge">● LIVE</span>
                </div>
                <p>Real-time system monitoring and resource utilization</p>
              </div>
              <div className="admin-cards-grid health-grid">
                <div className="admin-card system-health featured-card gradient-card">
                  <div className="card-header-row">
                    <div className="card-icon pulse-animation">💚</div>
                    <div className="card-status-badge badge-success">EXCELLENT</div>
                  </div>
                  <div className="card-content">
                    <h3>System Health</h3>
                    <p className="card-value card-value-large">{stats.systemHealth}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">⏱️</span> Uptime: {stats.systemUptime}
                      </div>
                      <div className="trend-indicator trend-up">
                        <span className="trend-arrow">↑</span> 0.1%
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-card server-load">
                  <div className="card-header-row">
                    <div className="card-icon">📊</div>
                    <div className={`card-status-badge ${stats.serverLoad < 60 ? 'badge-success' : stats.serverLoad < 80 ? 'badge-warning' : 'badge-danger'}`}>
                      {stats.serverLoad < 60 ? 'NORMAL' : stats.serverLoad < 80 ? 'MODERATE' : 'HIGH'}
                    </div>
                  </div>
                  <div className="card-content">
                    <h3>Server Load</h3>
                    <p className="card-value">{formatPercentage(stats.serverLoad)}</p>
                    <div className="progress-bar-modern">
                      <div className="progress-fill-modern" style={{width: `${stats.serverLoad}%`}}>
                        <div className="progress-shine"></div>
                      </div>
                      <div className="progress-markers">
                        <span className="marker" style={{left: '25%'}}></span>
                        <span className="marker" style={{left: '50%'}}></span>
                        <span className="marker" style={{left: '75%'}}></span>
                      </div>
                    </div>
                    <div className="card-meta-row">
                      <div className="card-subtitle">CPU Utilization</div>
                    </div>
                  </div>
                </div>

                <div className="admin-card memory-usage">
                  <div className="card-header-row">
                    <div className="card-icon">🧠</div>
                    <div className={`card-status-badge ${stats.memoryUsage < 70 ? 'badge-success' : stats.memoryUsage < 85 ? 'badge-warning' : 'badge-danger'}`}>
                      {stats.memoryUsage < 70 ? 'OPTIMAL' : stats.memoryUsage < 85 ? 'MODERATE' : 'HIGH'}
                    </div>
                  </div>
                  <div className="card-content">
                    <h3>Memory Usage</h3>
                    <p className="card-value">{formatPercentage(stats.memoryUsage)}</p>
                    <div className="progress-bar-modern">
                      <div className="progress-fill-modern" style={{width: `${stats.memoryUsage}%`}}>
                        <div className="progress-shine"></div>
                      </div>
                      <div className="progress-markers">
                        <span className="marker" style={{left: '25%'}}></span>
                        <span className="marker" style={{left: '50%'}}></span>
                        <span className="marker" style={{left: '75%'}}></span>
                      </div>
                    </div>
                    <div className="card-meta-row">
                      <div className="card-subtitle">RAM Allocation</div>
                    </div>
                  </div>
                </div>

                <div className="admin-card disk-usage-card">
                  <div className="card-header-row">
                    <div className="card-icon">💿</div>
                    <div className={`card-status-badge ${stats.diskUsage < 70 ? 'badge-success' : stats.diskUsage < 85 ? 'badge-warning' : 'badge-danger'}`}>
                      {stats.diskUsage < 70 ? 'HEALTHY' : stats.diskUsage < 85 ? 'MODERATE' : 'CRITICAL'}
                    </div>
                  </div>
                  <div className="card-content">
                    <h3>Disk Usage</h3>
                    <p className="card-value">{formatPercentage(stats.diskUsage)}</p>
                    <div className="progress-bar-modern">
                      <div className="progress-fill-modern" style={{width: `${stats.diskUsage}%`}}>
                        <div className="progress-shine"></div>
                      </div>
                      <div className="progress-markers">
                        <span className="marker" style={{left: '25%'}}></span>
                        <span className="marker" style={{left: '50%'}}></span>
                        <span className="marker" style={{left: '75%'}}></span>
                      </div>
                    </div>
                    <div className="card-meta-row">
                      <div className="card-subtitle">Storage Space</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Management Section */}
            <div className="admin-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>👥 User Analytics</h2>
                  <span className="section-badge info-badge">ACTIVE MONITORING</span>
                </div>
                <p>User statistics, role distribution, and security monitoring</p>
              </div>
              <div className="admin-cards-grid users-grid">
                <div className="admin-card total-users enhanced-card">
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-blue">👥</div>
                  </div>
                  <div className="card-content">
                    <h3>Total Users</h3>
                    <p className="card-value card-value-highlight">{stats.totalUsers.toLocaleString()}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">📈</span> Registered users
                      </div>
                      <div className="trend-indicator trend-up">
                        <span className="trend-arrow">↑</span> 12%
                      </div>
                    </div>
                    <div className="mini-chart-line"></div>
                  </div>
                </div>

                <div className="admin-card blocked-users enhanced-card">
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-red">🚫</div>
                  </div>
                  <div className="card-content">
                    <h3>Blocked Users</h3>
                    <p className="card-value card-value-warning">{stats.blockedUsers}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">🔒</span> Security issues
                      </div>
                      {stats.blockedUsers > 0 && (
                        <div className="alert-pulse"></div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="admin-card active-sessions enhanced-card">
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-purple">🔐</div>
                  </div>
                  <div className="card-content">
                    <h3>Active Sessions</h3>
                    <p className="card-value card-value-success">{stats.activeSessions.toLocaleString()}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">👤</span> Current logins
                      </div>
                      <div className="live-indicator">
                        <span className="live-dot"></span> LIVE
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Role Distribution */}
              <div className="role-distribution-section">
                <h3 className="subsection-title">
                  <span className="subsection-icon">🎭</span> Role Distribution
                </h3>
                <div className="admin-cards-grid users-grid">
                  <div className="admin-card role-card manager-card">
                    <div className="role-card-header">
                      <div className="card-icon icon-gradient-gold">👔</div>
                      <div className="role-percentage">{((totalManagers / stats.totalUsers) * 100).toFixed(1)}%</div>
                    </div>
                    <div className="card-content">
                      <h3>Managers</h3>
                      <p className="card-value card-value-medium">{totalManagers}</p>
                      <div className="role-progress-bar">
                        <div className="role-progress-fill manager-fill" style={{width: `${(totalManagers / stats.totalUsers) * 100}%`}}></div>
                      </div>
                      <div className="card-subtitle">Active manager accounts</div>
                    </div>
                  </div>
                  <div className="admin-card role-card merchant-card">
                    <div className="role-card-header">
                      <div className="card-icon icon-gradient-green">🏪</div>
                      <div className="role-percentage">{((totalMerchants / stats.totalUsers) * 100).toFixed(1)}%</div>
                    </div>
                    <div className="card-content">
                      <h3>Merchants</h3>
                      <p className="card-value card-value-medium">{totalMerchants}</p>
                      <div className="role-progress-bar">
                        <div className="role-progress-fill merchant-fill" style={{width: `${(totalMerchants / stats.totalUsers) * 100}%`}}></div>
                      </div>
                      <div className="card-subtitle">Active merchant accounts</div>
                    </div>
                  </div>
                  <div className="admin-card role-card admin-card-role">
                    <div className="role-card-header">
                      <div className="card-icon icon-gradient-purple">👑</div>
                      <div className="role-percentage">{((totalAdmins / stats.totalUsers) * 100).toFixed(1)}%</div>
                    </div>
                    <div className="card-content">
                      <h3>Administrators</h3>
                      <p className="card-value card-value-medium">{totalAdmins}</p>
                      <div className="role-progress-bar">
                        <div className="role-progress-fill admin-fill" style={{width: `${(totalAdmins / stats.totalUsers) * 100}%`}}></div>
                      </div>
                      <div className="card-subtitle">Active admin accounts</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Overview Section */}
            <div className="admin-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>💼 Manager Financial Overview</h2>
                  <span className="section-badge success-badge">FINANCIAL TRACKING</span>
                </div>
                <p>Revenue tracking, transaction analytics, and financial performance</p>
              </div>
              <div className="admin-cards-grid financial-grid">
                <div className="admin-card total-revenue financial-card">
                  <div className="card-corner-badge badge-gold">CREDIT</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-gold pulse-slow">💰</div>
                  </div>
                  <div className="card-content">
                    <h3>Total Credit</h3>
                    <p className="card-value card-value-currency">{formatCurrency(managerTotalCredit)}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">📊</span> All Time Inflow
                      </div>
                      <div className="trend-indicator trend-up">
                        <span className="trend-arrow">↑</span> 8.2%
                      </div>
                    </div>
                    <div className="sparkline-container">
                      <div className="sparkline-bar" style={{height: '40%'}}></div>
                      <div className="sparkline-bar" style={{height: '60%'}}></div>
                      <div className="sparkline-bar" style={{height: '45%'}}></div>
                      <div className="sparkline-bar" style={{height: '80%'}}></div>
                      <div className="sparkline-bar" style={{height: '70%'}}></div>
                      <div className="sparkline-bar" style={{height: '90%'}}></div>
                      <div className="sparkline-bar" style={{height: '100%'}}></div>
                    </div>
                  </div>
                </div>

                <div className="admin-card today-revenue financial-card">
                  <div className="card-corner-badge badge-blue">DEBIT</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-blue">📈</div> 
                  </div>
                  <div className="card-content">
                    <h3>Total Debit</h3>
                    <p className="card-value card-value-currency">{formatCurrency(managerTotalDebit)}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">📉</span> All Time Outflow
                      </div>
                      <div className="trend-indicator trend-neutral">
                        <span className="trend-arrow">→</span> 3.1%
                      </div>
                    </div>
                    <div className="sparkline-container">
                      <div className="sparkline-bar" style={{height: '60%'}}></div>
                      <div className="sparkline-bar" style={{height: '50%'}}></div>
                      <div className="sparkline-bar" style={{height: '70%'}}></div>
                      <div className="sparkline-bar" style={{height: '55%'}}></div>
                      <div className="sparkline-bar" style={{height: '65%'}}></div>
                      <div className="sparkline-bar" style={{height: '60%'}}></div>
                      <div className="sparkline-bar" style={{height: '75%'}}></div>
                    </div>
                  </div>
                </div>

                <div className="admin-card error-rate financial-card">
                  <div className="card-corner-badge badge-purple">VOLUME</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-purple">🔄</div>
                  </div>
                  <div className="card-content">
                    <h3>Total Transactions</h3>
                    <p className="card-value card-value-number">{managerTotalTransactions.toLocaleString()}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">💳</span> Total Volume
                      </div>
                      <div className="transaction-badge">
                        {managerTotalTransactions > 1000 ? 'HIGH' : 'MEDIUM'} VOLUME
                      </div>
                    </div>
                    <div className="stat-comparison">
                      <div className="stat-item">
                        <span className="stat-label">Avg/Day</span>
                        <span className="stat-value">{Math.round(managerTotalTransactions / 30)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Resources Section */}
            <div className="admin-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>🏪 Merchant Analytics</h2>
                  <span className="section-badge merchant-badge">TRANSACTION TRACKING</span>
                </div>
                <p>Merchant statistics, transaction monitoring, and payout analytics</p>
              </div>
              <div className="admin-cards-grid resources-grid">
                <div className="admin-card api-calls merchant-stats-card">
                  <div className="card-corner-badge badge-orange">DEBIT</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-orange">📡</div>
                  </div>
                  <div className="card-content">
                    <h3>Total Payout Debit</h3>
                    <p className="card-value card-value-currency">{formatCurrency(merchantTotalPayoutDebit)}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">💸</span> Total Disbursed
                      </div>
                      <div className="trend-indicator trend-up">
                        <span className="trend-arrow">↑</span> 15.4%
                      </div>
                    </div>
                    <div className="mini-stats-grid">
                      <div className="mini-stat">
                        <span className="mini-stat-value">{merchantTotalTransactionsDone}</span>
                        <span className="mini-stat-label">Transactions</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-card database-size merchant-stats-card">
                  <div className="card-corner-badge badge-teal">COMPLETED</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-teal">✅</div>
                  </div>
                  <div className="card-content">
                    <h3>Transactions Done</h3>
                    <p className="card-value card-value-number">{merchantTotalTransactionsDone.toLocaleString()}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">📊</span> Successfully Completed
                      </div>
                    </div>
                    <div className="completion-rate">
                      <span className="completion-label">Success Rate</span>
                      <span className="completion-value">{merchantTotalTransactionsDone > 0 ? ((merchantTotalTransactionsDone / (merchantTotalTransactionsDone + stats.failedPayouts)) * 100).toFixed(1) : 0}%</span>
                    </div>
                  </div>
                </div>

                <div className="admin-card disk-usage merchant-stats-card">
                  <div className="card-corner-badge badge-green">FEES</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-green">💰</div>
                  </div>
                  <div className="card-content">
                    <h3>Transaction Charges</h3>
                    <p className="card-value card-value-currency">{formatCurrency(merchantTotalTransactionCharges)}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">💳</span> Total Fees Collected
                      </div>
                    </div>
                    <div className="fee-breakdown">
                      <span className="fee-label">Avg Fee per Transaction</span>
                      <span className="fee-value">{formatCurrency(merchantTotalTransactionsDone > 0 ? merchantTotalTransactionCharges / merchantTotalTransactionsDone : 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payout Management Section */}
            <div className="admin-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>💸 Payout Management</h2>
                  <span className="section-badge payout-badge">REAL-TIME STATUS</span>
                </div>
                <p>Payout statistics, status tracking, and transaction monitoring</p>
              </div>
              <div className="admin-cards-grid payouts-grid">
                <div className="admin-card total-payouts payout-stat-card">
                  <div className="card-corner-badge badge-blue-light">TOTAL</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-blue-bright">💸</div>
                  </div>
                  <div className="card-content">
                    <h3>Total Payouts</h3>
                    <p className="card-value card-value-large-number">{stats.totalPayouts.toLocaleString()}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">📈</span> All Time Payouts
                      </div>
                    </div>
                    <div className="payout-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-dot success-dot"></span>
                        <span className="breakdown-label">Success:</span>
                        <span className="breakdown-value">{stats.completedPayouts || (stats.totalPayouts - stats.pendingPayouts - stats.failedPayouts)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-card pending-payouts payout-stat-card">
                  <div className="card-corner-badge badge-warning">PENDING</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-warning pulse-animation">⏳</div>
                  </div>
                  <div className="card-content">
                    <h3>Pending Payouts</h3>
                    <p className="card-value card-value-warning-color">{stats.pendingPayouts}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">⏱️</span> Awaiting Processing
                      </div>
                      {stats.pendingPayouts > 0 && (
                        <div className="attention-badge">NEEDS ATTENTION</div>
                      )}
                    </div>
                    <div className="status-indicator-row">
                      <div className="status-dot-animated"></div>
                      <span className="status-text">Processing...</span>
                    </div>
                  </div>
                </div>

                <div className="admin-card failed-payouts payout-stat-card">
                  <div className="card-corner-badge badge-danger">FAILED</div>
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-danger">{stats.failedPayouts > 0 ? '⚠️' : '✅'}</div>
                  </div>
                  <div className="card-content">
                    <h3>Failed Payouts</h3> 
                    <p className="card-value card-value-danger-color">{stats.failedPayouts}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">❌</span> Transaction Failures
                      </div>
                    </div>
                    <div className="failure-rate-container">
                      <span className="failure-label">Failure Rate</span>
                      <span className={`failure-rate ${stats.totalPayouts > 0 && (stats.failedPayouts / stats.totalPayouts * 100) > 5 ? 'high-failure' : 'low-failure'}`}>
                        {stats.totalPayouts > 0 ? ((stats.failedPayouts / stats.totalPayouts) * 100).toFixed(2) : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Management Tab Content */}
        {activeTab === 'users' && <UserManagement />}

        {/* Report Management Tab Content */}
        {activeTab === 'reports' && <ReportManagement />}

        {/* Fraud Management Tab Content */}
        {activeTab === 'fraud' && <FraudManagement />}

        {/* Payout Error Logs Tab Content */}
        {activeTab === 'payout-errors' && <PayoutErrorLogs />}

        {/* Admin Wallet Tab Content */}
        {activeTab === 'wallet' && <AdminWalletContent />}

        {/* System Control Tab Content */}
        {activeTab === 'system' && (
          <div className="admin-dashboard-content">
            <div className="admin-section system-control-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>⚙️ System Control</h2>
                  <span className="section-badge control-badge">ADMIN CONTROL</span>
                </div>
                <p>Advanced system controls, maintenance management, and security settings</p>
              </div>

              {/* Control Cards Grid: IP Security, Backup Status, Maintenance Mode */}
              <div className="admin-cards-grid system-control-grid">
                <div
                  className="admin-card system-control-card security-card"
                  onClick={openIpSecurityModal}
                >
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-green pulse-slow">🛡️</div>
                    <div className="card-status-badge badge-success">SECURE</div>
                  </div>
                  <div className="card-content">
                    <h3>IP Security</h3>
                    <p className="card-value card-value-secure">Secure</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">✓</span> All systems normal
                      </div>
                      <div className="expand-indicator">
                        ⧉ OPEN
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="admin-card system-control-card backup-card"
                  onClick={openBackupModal}
                >
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-blue pulse-slow">💾</div>
                    <div className="card-status-badge badge-success">READY</div>
                  </div>
                  <div className="card-content">
                    <h3>Backup Status</h3>
                    <p className="card-value card-value-ready">Ready</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">🕐</span> Last: 2 hours ago
                      </div>
                      <div className="expand-indicator">
                        ⧉ OPEN
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="admin-card system-control-card maintenance-card"
                  onClick={openMaintenanceModal}
                >
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-warning pulse-animation">🔧</div>
                    <div className={`card-status-badge ${maintenanceSettings.enabled ? 'badge-success' : 'badge-danger'}`}>
                      {maintenanceSettings.enabled ? 'ACTIVE' : 'INACTIVE'}
                    </div>
                  </div>
                  <div className="card-content">
                    <h3>Maintenance Mode</h3>
                    <p className="card-value card-value-status">{maintenanceSettings.enabled ? 'Online' : 'Offline'}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">⚡</span> System status
                      </div>
                      <div className="expand-indicator">
                        ⧉ OPEN
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Maintenance Mode Modal */}
              {activeSystemControlModal === 'maintenance' && (
                <div className="modal-overlay" onClick={closeSystemControlModal}>
                  <div className="modal-content settings-config-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close settings-modal-close" onClick={closeSystemControlModal}>×</button>
                    <div className="maintenance-config-container settings-modal-panel">
                  <div className="maintenance-config-header">
                    <h3>
                      <span className="config-icon">🔧</span>
                      Maintenance Mode Configuration
                    </h3>
                    <span className={`maintenance-status-badge ${maintenanceSettings.enabled ? 'status-active' : 'status-inactive'}`}>
                      {maintenanceSettings.enabled ? '● ACTIVE' : '● INACTIVE'}
                    </span>
                  </div>

                  <div className="maintenance-config-body">
                    <div className="config-form-group">
                      <label htmlFor="maintenance-title">
                        <span className="label-icon">📝</span>
                        Maintenance Title
                      </label>
                      <input
                        id="maintenance-title"
                        type="text"
                        className="config-input"
                        value={maintenanceSettings.title}
                        onChange={(e) => setMaintenanceSettings(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="System Maintenance"
                      />
                    </div>

                    <div className="config-form-group">
                      <label htmlFor="maintenance-description">
                        <span className="label-icon">💬</span>
                        Maintenance Description
                      </label>
                      <textarea
                        id="maintenance-description"
                        className="config-textarea"
                        value={maintenanceSettings.description}
                        onChange={(e) => setMaintenanceSettings(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="We are currently performing scheduled maintenance. Please check back later."
                        rows={4}
                      />
                    </div>

                    <div className="config-actions">
                      <button
                        className="config-btn config-btn-save"
                        onClick={saveMaintenanceSettings}
                        disabled={maintenanceLoading}
                      >
                        <span className="btn-icon">{maintenanceLoading ? '⏳' : '💾'}</span>
                        {maintenanceLoading ? 'Saving...' : 'Save Settings'}
                      </button>
                      <button
                        className={`config-btn ${maintenanceSettings.enabled ? 'config-btn-stop' : 'config-btn-start'}`}
                        onClick={toggleMaintenanceMode}
                        disabled={maintenanceLoading}
                      >
                        <span className="btn-icon">{maintenanceLoading ? '⏳' : maintenanceSettings.enabled ? '⛔' : '▶️'}</span>
                        {maintenanceLoading ? 'Processing...' : (maintenanceSettings.enabled ? 'Stop Maintenance' : 'Start Maintenance')}
                      </button>
                    </div>

                    <div className="maintenance-preview">
                      <h4>
                        <span className="preview-icon">👁️</span>
                        Preview
                      </h4>
                      <div className="preview-box">
                        <div className="preview-title">{maintenanceSettings.title || 'System Maintenance'}</div>
                        <div className="preview-description">{maintenanceSettings.description || 'We are currently performing scheduled maintenance.'}</div>
                      </div>
                    </div>
                  </div>
                </div>
                  </div>
                </div>
              )}

              {/* Backup Status Modal */}
              {activeSystemControlModal === 'backup' && (
                <div className="modal-overlay" onClick={closeSystemControlModal}>
                  <div className="modal-content settings-config-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close settings-modal-close" onClick={closeSystemControlModal}>×</button>
                    <div className="maintenance-config-container settings-modal-panel">
                  <div className="maintenance-config-header">
                    <h3>
                      <span className="config-icon">💾</span>
                      Backup Status
                    </h3>
                    <span className="maintenance-status-badge status-active">● READY</span>
                  </div>
                  <div className="maintenance-config-body">
                    {(backupSettingsLoading || backupTablesLoading) && (
                      <p className="backup-info">Loading backup settings...</p>
                    )}
                    {!backupSettingsLoading && !backupTablesLoading && (
                      <>
                        <p className="backup-info">
                          <span className="meta-icon">🕐</span> Last backup: <strong>{backupSettings.last_backup || 'Never'}</strong>
                        </p>
                        <p className="backup-info">
                          <span className="meta-icon">📁</span> Backup location: <code>{backupSettings.backup_folder}</code>
                        </p>

                        <div className="config-form-group">
                          <label className="config-label">Zip password (saved for backups)</label>
                          <div className="backup-password-row">
                            <input
                              type="password"
                              className="config-input"
                              placeholder="Set zip password for backup files"
                              value={backupSettings.zip_password}
                              onChange={(e) => setBackupSettings(prev => ({ ...prev, zip_password: e.target.value }))}
                            />
                            <button
                              type="button"
                              className="config-btn config-btn-start"
                              onClick={saveBackupPassword}
                              disabled={backupSavingPassword}
                            >
                              {backupSavingPassword ? 'Saving...' : 'Save password'}
                            </button>
                          </div>
                          <p className="backup-info muted">Saved password is used when you create a backup with &quot;Use saved password&quot;.</p>
                        </div>

                        <div className="config-form-group">
                          <label className="config-label">Backup scope</label>
                          <div className="backup-scope-options">
                            <label className="backup-scope-option">
                              <input
                                type="radio"
                                name="backupScope"
                                checked={backupScope === 'full'}
                                onChange={() => setBackupScope('full')}
                              />
                              <span>Full database</span>
                            </label>
                            <label className="backup-scope-option">
                              <input
                                type="radio"
                                name="backupScope"
                                checked={backupScope === 'tables'}
                                onChange={() => setBackupScope('tables')}
                              />
                              <span>Selected tables only</span>
                            </label>
                          </div>
                          {backupScope === 'tables' && (
                            <div className="backup-tables-select">
                              <select
                                multiple
                                className="config-input"
                                value={backupSelectedTables}
                                onChange={(e) => {
                                  const selected = Array.from(e.target.selectedOptions, o => o.value);
                                  setBackupSelectedTables(selected);
                                }}
                                style={{ minHeight: 120 }}
                              >
                                {backupTables.map((t) => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                              <p className="backup-info muted">Hold Ctrl/Cmd to select multiple tables.</p>
                            </div>
                          )}
                        </div>

                        <div className="config-form-group">
                          <label className="config-label">Password for this backup (optional)</label>
                          <input
                            type="password"
                            className="config-input"
                            placeholder="Leave empty to use saved password"
                            value={backupPasswordInput}
                            onChange={(e) => setBackupPasswordInput(e.target.value)}
                          />
                          <p className="backup-info muted">Override saved password for this backup only, or leave empty to use saved password.</p>
                        </div>

                        <div className="config-form-group">
                          <button
                            type="button"
                            className="config-btn config-btn-start"
                            onClick={createBackup}
                            disabled={backupCreating || (backupScope === 'tables' && backupSelectedTables.length === 0)}
                          >
                            {backupCreating ? 'Creating backup...' : 'Create backup (zip in backup folder)'}
                          </button>
                        </div>

                        <div className="config-form-group">
                          <label className="config-label">Backup files in folder</label>
                          {backupListLoading && <p className="backup-info">Loading list...</p>}
                          {!backupListLoading && backupList.length === 0 && (
                            <p className="backup-info muted">No backup files yet.</p>
                          )}
                          {!backupListLoading && backupList.length > 0 && (
                            <ul className="backup-list">
                              {backupList.map((f) => (
                                <li key={f.name} className="backup-list-item">
                                  <span className="backup-list-name">{f.name}</span>
                                  <span className="backup-list-meta">{(f.size / 1024).toFixed(1)} KB · {f.created}</span>
                                  <button
                                    type="button"
                                    className="config-btn config-btn-start small"
                                    onClick={() => downloadBackupFile(f.name)}
                                  >
                                    Download
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                  </div>
                </div>
              )}

              {/* IP Security Modal */}
              {activeSystemControlModal === 'ip-security' && (
                <div className="modal-overlay" onClick={closeSystemControlModal}>
                  <div className="modal-content settings-config-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close settings-modal-close" onClick={closeSystemControlModal}>×</button>
                    <div className="maintenance-config-container settings-modal-panel">
                  <div className="maintenance-config-header">
                    <h3>
                      <span className="config-icon">🛡️</span>
                      IP Security
                    </h3>
                    <span className="maintenance-status-badge status-active">● CONFIG</span>
                  </div>
                  <div className="maintenance-config-body">
                    {ipSecurityLoading && (
                      <p className="backup-info">Loading...</p>
                    )}
                    {!ipSecurityLoading && (
                      <>
                        <div className="ip-security-layout">
                          <div className="ip-security-col ip-security-col-left">
                            {/* 1) Admin login whitelist */}
                            <div className="config-form-group ip-security-option">
                              <label className="ip-security-label">
                                <input
                                  type="checkbox"
                                  checked={ipSecurity.admin_whitelist_enabled}
                                  onChange={(e) => setIpSecurity(prev => ({ ...prev, admin_whitelist_enabled: e.target.checked }))}
                                />
                                <span>1. Admin login – whitelist IPs only</span>
                              </label>
                              <p className="backup-info muted">When enabled, only whitelisted IPs can log in to Admin accounts.</p>
                              <div className="ip-list-editor">
                                {ipSecurity.admin_whitelist_ips.map((ip, i) => (
                                  <div key={i} className="ip-list-row">
                                    <input
                                      type="text"
                                      className="config-input ip-input"
                                      value={ip}
                                      onChange={(e) => setIpSecurity(prev => ({
                                        ...prev,
                                        admin_whitelist_ips: prev.admin_whitelist_ips.map((x, j) => j === i ? e.target.value : x)
                                      }))}
                                      placeholder="e.g. 192.168.1.1"
                                    />
                                    <button type="button" className="config-btn config-btn-stop small" onClick={() => setIpSecurity(prev => ({ ...prev, admin_whitelist_ips: prev.admin_whitelist_ips.filter((_, j) => j !== i) }))}>Remove</button>
                                  </div>
                                ))}
                                <button type="button" className="config-btn config-btn-start small" onClick={() => setIpSecurity(prev => ({ ...prev, admin_whitelist_ips: [...prev.admin_whitelist_ips, ''] }))}>+ Add IP</button>
                              </div>
                            </div>
                          </div>

                          <div className="ip-security-col ip-security-col-right">
                            {/* 2) IP banned for merchant/manager */}
                            <div className="config-form-group ip-security-option">
                              <label className="ip-security-label">
                                <input
                                  type="checkbox"
                                  checked={ipSecurity.ip_ban_enabled}
                                  onChange={(e) => setIpSecurity(prev => ({ ...prev, ip_ban_enabled: e.target.checked }))}
                                />
                                <span>2. IP ban – block login/register for Merchant & Manager</span>
                              </label>
                              <p className="backup-info muted">When enabled, banned IPs cannot login or register as Merchant/Manager (same IP, different account still blocked).</p>
                              <div className="ip-list-editor">
                                {ipSecurity.banned_ips.map((ip, i) => (
                                  <div key={i} className="ip-list-row">
                                    <input
                                      type="text"
                                      className="config-input ip-input"
                                      value={ip}
                                      onChange={(e) => setIpSecurity(prev => ({
                                        ...prev,
                                        banned_ips: prev.banned_ips.map((x, j) => j === i ? e.target.value : x)
                                      }))}
                                      placeholder="e.g. 10.0.0.1"
                                    />
                                    <button type="button" className="config-btn config-btn-stop small" onClick={() => setIpSecurity(prev => ({ ...prev, banned_ips: prev.banned_ips.filter((_, j) => j !== i) }))}>Remove</button>
                                  </div>
                                ))}
                                <button type="button" className="config-btn config-btn-start small" onClick={() => setIpSecurity(prev => ({ ...prev, banned_ips: [...prev.banned_ips, ''] }))}>+ Add banned IP</button>
                              </div>
                            </div>

                            {/* 3) Allowed IP range or country */}
                            <div className="config-form-group ip-security-option">
                              <label className="ip-security-label">
                                <input
                                  type="checkbox"
                                  checked={ipSecurity.allowed_ip_or_country_enabled}
                                  onChange={(e) => setIpSecurity(prev => ({ ...prev, allowed_ip_or_country_enabled: e.target.checked }))}
                                />
                                <span>3. Allowed IP range or country</span>
                              </label>
                              <p className="backup-info muted">When enabled, only these IP ranges (CIDR) or countries can access login/register. Leave empty to allow all when disabled.</p>
                              <div className="ip-list-editor">
                                <p className="backup-info" style={{ marginBottom: '0.5rem' }}>IP ranges (CIDR, e.g. 192.168.0.0/24):</p>
                                {ipSecurity.allowed_ip_ranges.map((r, i) => (
                                  <div key={i} className="ip-list-row">
                                    <input
                                      type="text"
                                      className="config-input ip-input"
                                      value={r}
                                      onChange={(e) => setIpSecurity(prev => ({
                                        ...prev,
                                        allowed_ip_ranges: prev.allowed_ip_ranges.map((x, j) => j === i ? e.target.value : x)
                                      }))}
                                      placeholder="e.g. 192.168.0.0/24"
                                    />
                                    <button type="button" className="config-btn config-btn-stop small" onClick={() => setIpSecurity(prev => ({ ...prev, allowed_ip_ranges: prev.allowed_ip_ranges.filter((_, j) => j !== i) }))}>Remove</button>
                                  </div>
                                ))}
                                <button type="button" className="config-btn config-btn-start small" onClick={() => setIpSecurity(prev => ({ ...prev, allowed_ip_ranges: [...prev.allowed_ip_ranges, ''] }))}>+ Add range</button>
                              </div>
                              <div className="ip-list-editor" style={{ marginTop: '1rem' }}>
                                <p className="backup-info" style={{ marginBottom: '0.5rem' }}>Countries (ISO codes, e.g. IN, US):</p>
                                <input
                                  type="text"
                                  className="config-input"
                                  value={ipSecurity.allowed_countries.join(', ')}
                                  onChange={(e) => setIpSecurity(prev => ({
                                    ...prev,
                                    allowed_countries: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
                                  }))}
                                  placeholder="IN, US, GB"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="config-actions">
                          <button
                            className="config-btn config-btn-save"
                            onClick={saveIpSecuritySettings}
                            disabled={ipSecurityLoading}
                          >
                            {ipSecurityLoading ? 'Saving...' : 'Save IP Security Settings'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Activity Tab Content */}
        {activeTab === 'activity' && (
          <div className="admin-dashboard-content">
            <div className="admin-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>📋 User Activity</h2>
                  <span className="section-badge">LOGS</span>
                </div>
                <p>Login, logout, and payout activity for all users with IP address.</p>
              </div>
              <div className="activity-logs-actions">
                <button
                  className="btn-secondary"
                  onClick={() => loadActivityLogs(activityLogsPagination.currentPage)}
                  disabled={activityLogsLoading}
                >
                  {activityLogsLoading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="logs-table-container">
                {activityLogsLoading && activityLogs.length === 0 ? (
                  <div className="admin-loading-inline">
                    <div className="loading-spinner"></div>
                    <p>Loading activity logs...</p>
                  </div>
                ) : activityLogs.length === 0 ? (
                  <p className="no-data-message">No activity logs yet. Login, logout, and payout events will appear here.</p>
                ) : (
                  <>
                    <table className="logs-table">
                      <thead>
                        <tr>
                          <th>Date / Time</th>
                          <th>User</th>
                          <th>Activity Type</th>
                          <th>Device</th>
                          <th>Description</th>
                          <th>IP Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityLogs.map((log, index) => (
                          <tr key={log.id != null ? log.id : `act-${index}`} className="log-row">
                            <td className="log-time-cell">
                              {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                            </td>
                            <td className="log-merchant-cell">
                              <span className="activity-user-name">{log.user_name || '—'}</span>
                              {log.email && <span className="activity-user-email">{log.email}</span>}
                            </td>
                            <td className="log-id-cell">
                              <span className={`activity-type-badge type-${(log.type || '').toLowerCase()}`}>
                                {log.type === 'login' ? 'Login' : log.type === 'logout' ? 'Logout' : log.type === 'payout' ? 'Payout' : log.type || '—'}
                              </span>
                            </td>
                            <td className="log-merchant-cell">
                              <span className={`activity-device-badge device-${(log.device || 'web').toLowerCase()}`}>
                                {log.device === 'Tablet' ? 'Tablet' : log.device === 'Mobile' ? 'Mobile' : log.device === 'Web' ? 'Web' : log.device || '—'}
                              </span>
                            </td>
                            <td className="log-description-cell">{log.description || '—'}</td>
                            <td className="log-transaction-cell">{log.ip_address || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {activityLogsPagination.totalPages > 1 && (
                      <div className="logs-pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => loadActivityLogs(activityLogsPagination.currentPage - 1)}
                          disabled={activityLogsPagination.currentPage <= 1 || activityLogsLoading}
                        >
                          ← Previous
                        </button>
                        <div className="pagination-info">
                          <span>Page {activityLogsPagination.currentPage} of {activityLogsPagination.totalPages}</span>
                          <span className="pagination-total">({activityLogsPagination.totalRecords} records)</span>
                        </div>
                        <button
                          className="pagination-btn"
                          onClick={() => loadActivityLogs(activityLogsPagination.currentPage + 1)}
                          disabled={activityLogsPagination.currentPage >= activityLogsPagination.totalPages || activityLogsLoading}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Status Checker Tab Content */}
        {activeTab === 'status-checker' && (
          <div className="admin-dashboard-content">
            <div className="admin-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>🔍 Status Checker</h2>
                  <span className="section-badge">TOOLS</span>
                </div>
                <p>Check payout or transaction status by reference ID. Credentials are stored temporarily in this browser and persist after reload.</p>
              </div>
              <div className="status-checker-layout">
                <div className="maintenance-config-container status-checker-form-compact status-checker-form-col">
                  <div className="maintenance-config-body">
                    <div className="config-form-group">
                      <label htmlFor="status-checker-merchant-ref">
                        <span className="label-icon">📋</span> Merchant Reference Number
                      </label>
                      <div className="flex gap-2 items-center">
                        <input
                          id="status-checker-merchant-ref"
                          type="text"
                          className="config-input flex-1"
                          placeholder="e.g. PAYOUT_1773252663056"
                          value={statusCheckerForm.merchantRef}
                          onChange={(e) => setStatusCheckerForm(prev => ({ ...prev, merchantRef: e.target.value }))}
                          onBlur={() => fetchStatusCheckerCredentials()}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="config-btn config-btn-save status-checker-lookup-btn"
                          onClick={fetchStatusCheckerCredentials}
                          disabled={statusCheckerLookupLoading || !statusCheckerForm.merchantRef.trim()}
                          title="Auto-fill credentials from payout"
                        >
                          {statusCheckerLookupLoading ? '…' : 'Lookup'}
                        </button>
                      </div>
                    </div>
                    <div className="config-form-group">
                      <label htmlFor="status-checker-client-id">
                        <span className="label-icon">🆔</span> Client ID
                      </label>
                      <input
                        id="status-checker-client-id"
                        type="text"
                        className="config-input"
                        placeholder="Viyona client_id"
                        value={statusCheckerForm.clientId}
                        onChange={(e) => setStatusCheckerForm(prev => ({ ...prev, clientId: e.target.value }))}
                      />
                    </div>
                    <div className="config-form-group">
                      <label htmlFor="status-checker-client-secret">
                        <span className="label-icon">🔑</span> Client Secret
                      </label>
                      <div className="status-checker-password-wrap">
                        <input
                          id="status-checker-client-secret"
                          type={showClientSecret ? 'text' : 'password'}
                          className="config-input"
                          placeholder="Viyona client_secret"
                          value={statusCheckerForm.clientSecret}
                          onChange={(e) => setStatusCheckerForm(prev => ({ ...prev, clientSecret: e.target.value }))}
                        />
                        <button
                          type="button"
                          className="status-checker-toggle-visibility"
                          onClick={() => setShowClientSecret(prev => !prev)}
                          title={showClientSecret ? 'Hide' : 'Show'}
                          aria-label={showClientSecret ? 'Hide client secret' : 'Show client secret'}
                        >
                          {showClientSecret ? '🔒' : '👁️'}
                        </button>
                      </div>
                    </div>
                    <div className="config-form-group">
                      <label htmlFor="status-checker-api-key">
                        <span className="label-icon">🔐</span> Api Key
                      </label>
                      <div className="status-checker-password-wrap">
                        <input
                          id="status-checker-api-key"
                          type={showApiKey ? 'text' : 'password'}
                          className="config-input"
                          placeholder="Viyona api_key"
                          value={statusCheckerForm.apiKey}
                          onChange={(e) => setStatusCheckerForm(prev => ({ ...prev, apiKey: e.target.value }))}
                        />
                        <button
                          type="button"
                          className="status-checker-toggle-visibility"
                          onClick={() => setShowApiKey(prev => !prev)}
                          title={showApiKey ? 'Hide' : 'Show'}
                          aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKey ? '🔒' : '👁️'}
                        </button>
                      </div>
                    </div>
                    <div className="config-form-group">
                      <label htmlFor="status-checker-transaction-date">
                        <span className="label-icon">📅</span> Transaction Date
                      </label>
                      <input
                        id="status-checker-transaction-date"
                        type="text"
                        className="config-input"
                        placeholder="YYYY-MM-DD or YYYY-MM-DD HH:MM:SS"
                        value={statusCheckerForm.transactionDate}
                        onChange={(e) => setStatusCheckerForm(prev => ({ ...prev, transactionDate: e.target.value }))}
                      />
                    </div>
                    <div className="config-actions status-checker-actions">
                      <button
                        className="config-btn config-btn-save"
                        onClick={runStatusChecker}
                        disabled={statusCheckerLoading}
                      >
                        <span className="btn-icon">{statusCheckerLoading ? '⏳' : '🔍'}</span>
                        {statusCheckerLoading ? 'Checking...' : 'Check Status'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="status-checker-response-panel">
                  {statusCheckerResult !== null ? (
                    <div className="maintenance-config-container status-checker-response-box">
                      <div className="maintenance-config-header">
                        <h3>
                          <span className="config-icon">{statusCheckerResult.success ? '✅' : '❌'}</span>
                          {statusCheckerResult.success ? 'Viyona Status' : 'Error'}
                        </h3>
                      </div>
                      <div className="maintenance-config-body">
                        {statusCheckerResult.data?.liveStatus && (
                          <p className="backup-info"><strong>Status:</strong> {statusCheckerResult.data.liveStatus}</p>
                        )}
                        {statusCheckerResult.message && (
                          <p className="backup-info"><strong>Message:</strong> {statusCheckerResult.message}</p>
                        )}
                        {statusCheckerResult.data?.viyonaTransactionId && (
                          <p className="backup-info"><strong>Viyona Transaction ID:</strong> {statusCheckerResult.data.viyonaTransactionId}</p>
                        )}
                        {statusCheckerResult.data?.bankReferenceNumber && (
                          <p className="backup-info"><strong>Bank Reference:</strong> {statusCheckerResult.data.bankReferenceNumber}</p>
                        )}
                        {statusCheckerResult.data?.errorMessage && (
                          <p className="backup-info"><strong>Error Message:</strong> {statusCheckerResult.data.errorMessage}</p>
                        )}
                        {statusCheckerResult.data?.fullResponse != null && (
                          <div style={{ marginTop: '1rem' }}>
                            <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>Full response from Viyona</h4>
                            <pre className="config-input" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '400px', overflow: 'auto', fontSize: '0.85rem' }}>
                              {JSON.stringify(statusCheckerResult.data.fullResponse, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="status-checker-response-placeholder">
                      <span className="status-checker-placeholder-icon">🔍</span>
                      <p>Fill the form and click <strong>Check Status</strong> to see the response here.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab Content */}
        {activeTab === 'settings' && (
          <div className="admin-dashboard-content">
            <div className="admin-section settings-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <h2>🔧 Settings</h2>
                  <span className="section-badge settings-badge">CONFIGURATION</span>
                </div>
                <p>System configuration, automation, and preferences</p>
              </div>

              {/* Settings Cards Grid */}
              <div className="admin-cards-grid settings-grid">
                <div
                  className="admin-card settings-card security-settings-card"
                  onClick={openSecuritySettingsModal}
                >
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-green pulse-slow">🔐</div>
                    <div className="card-status-badge badge-success">ENABLED</div>
                  </div>
                  <div className="card-content">
                    <h3>Security Settings</h3>
                    <p className="card-value card-value-secure">Enabled</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">🔒</span> Authentication & access
                      </div>
                      <div className="expand-indicator">
                        ⧉ OPEN
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-card settings-card analytics-card">
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-blue pulse-slow">📊</div>
                    <div className="card-status-badge badge-success">ACTIVE</div>
                  </div>
                  <div className="card-content">
                    <h3>Analytics</h3>
                    <p className="card-value card-value-analytics">Active</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">📈</span> Data collection enabled
                      </div>
                      <div className="analytics-indicator">📡</div>
                    </div>
                  </div>
                </div>

                <div
                  className="admin-card settings-card app-settings-card"
                  onClick={openAppSettingsModal}
                >
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-indigo pulse-slow">📱</div>
                    <div className="card-status-badge badge-success">MOBILE APP</div>
                  </div>
                  <div className="card-content">
                    <h3>APP SETTINGS</h3>
                    <p className="card-value card-value-app">Manage</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">⬇️</span> Links & APK versions
                      </div>
                      <div className="expand-indicator">⧉ OPEN</div>
                    </div>
                  </div>
                </div>

                <div
                  className="admin-card settings-card cron-card"
                  onClick={openCronSettingsModal}
                >
                  <div className="card-header-row">
                    <div className="card-icon icon-gradient-purple-dark pulse-animation">⚙️</div>
                    <div className={`card-status-badge ${cronEnabled ? 'badge-success' : 'badge-danger'}`}>
                      {cronEnabled ? 'ENABLED' : 'DISABLED'}
                    </div>
                  </div>
                  <div className="card-content">
                    <h3>CRON Automation</h3>
                    <p className="card-value card-value-cron">{cronEnabled ? 'Enabled' : 'Disabled'}</p>
                    <div className="card-meta-row">
                      <div className="card-subtitle">
                        <span className="meta-icon">⏰</span> Auto-release automation
                      </div>
                      <div className="expand-indicator">
                        ⧉ OPEN
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CRON Configuration Modal */}
              {activeSettingsModal === 'cron' && (
                <div className="modal-overlay" onClick={closeSettingsModal}>
                  <div className="modal-content settings-config-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close settings-modal-close" onClick={closeSettingsModal}>×</button>
                    <div className="cron-config-container settings-modal-panel">
                  <div className="cron-config-header">
                    <h3>
                      <span className="config-icon">⚙️</span>
                      CRON Automation Configuration
                    </h3>
                    <span className={`cron-status-badge ${cronEnabled ? 'status-active' : 'status-inactive'}`}>
                      {cronEnabled ? '● ENABLED' : '● DISABLED'}
                    </span>
                  </div>

                  <div className="cron-config-body">
                    {/* CRON Enable & Expression */}
                    <div className="cron-main-settings">
                      <div className="cron-toggle-group">
                        <label className="cron-checkbox-label">
                          <input
                            type="checkbox"
                            className="cron-checkbox"
                            checked={cronEnabled}
                            onChange={(e) => setCronEnabled(e.target.checked)}
                          />
                          <span className="checkbox-text">
                            <span className="checkbox-icon">⚡</span>
                            Enable Auto-Release CRON
                          </span>
                        </label>
                      </div>

                      <div className="cron-expression-group">
                        <label htmlFor="cron-expression">
                          <span className="label-icon">⏰</span>
                          CRON Expression
                        </label>
                        <div className="expression-input-wrapper">
                          <input
                            id="cron-expression"
                            type="text"
                            className="config-input"
                            value={cronExpression}
                            onChange={(e) => setCronExpression(e.target.value)}
                            placeholder="*/5 * * * *"
                          />
                          <button
                            className="config-btn config-btn-save"
                            onClick={saveCronSettings}
                            disabled={cronLoading}
                          >
                            <span className="btn-icon">{cronLoading ? '⏳' : '💾'}</span>
                            {cronLoading ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                        <div className="cron-help-text">
                          <span className="help-icon">💡</span>
                          Use standard cron format. Example: <code className="cron-code">*/5 * * * *</code> runs every 5 minutes.
                          {cronLastSaved && <span className="last-saved">Last saved: {cronLastSaved}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Trigger Links Section */}
                    <div className="cron-triggers-section">
                      <div className="triggers-header">
                        <h4>
                          <span className="triggers-icon">🔗</span>
                          Trigger Links
                          <span className="triggers-count">{cronTriggers.length}/10</span>
                        </h4>
                      </div>

                      <div className="triggers-list">
                        {cronTriggers.map((trigger, idx) => (
                          <div key={idx} className="trigger-item">
                            <div className="trigger-number">{idx + 1}</div>
                            <div className="trigger-content">
                              <div className="trigger-field-group">
                                <label>
                                  <span className="field-icon">📝</span>
                                  Name:
                                </label>
                                <input
                                  type="text"
                                  className="trigger-input"
                                  value={trigger.name}
                                  onChange={(e) => updateCronTrigger(idx, 'name', e.target.value)}
                                  placeholder="Enter trigger name"
                                />
                              </div>

                              <div className="trigger-field-group">
                                <label>
                                  <span className="field-icon">🌐</span>
                                  URL:
                                </label>
                                <input
                                  type="url"
                                  className="trigger-input"
                                  value={trigger.url}
                                  onChange={(e) => updateCronTrigger(idx, 'url', e.target.value)}
                                  placeholder="https://example.com/cron/trigger"
                                />
                              </div>

                              <div className="trigger-field-group">
                                <label>
                                  <span className="field-icon">⏰</span>
                                  Expression:
                                </label>
                                <input
                                  type="text"
                                  className="trigger-input trigger-input-small"
                                  value={trigger.expression}
                                  onChange={(e) => updateCronTrigger(idx, 'expression', e.target.value)}
                                  placeholder="*/5 * * * *"
                                />
                              </div>

                              <div className="trigger-actions">
                                <button
                                  className="trigger-btn trigger-btn-run"
                                  onClick={() => runCronTrigger(trigger)}
                                  disabled={cronLoading || !trigger.url.trim()}
                                  title={`Run ${trigger.name || 'this trigger'} now`}
                                >
                                  <span className="btn-icon">▶️</span>
                                  RUN
                                </button>
                                <button
                                  className="trigger-btn trigger-btn-remove"
                                  onClick={() => removeCronTrigger(idx)}
                                  title="Remove this trigger"
                                >
                                  <span className="btn-icon">🗑️</span>
                                  Remove
                                </button>
                              </div>

                              <div className="trigger-status">
                                <span className={`status-pill ${trigger.url ? 'status-ready' : 'status-empty'}`}>
                                  {trigger.url ? '✓ Ready' : '⚠ No URL'}
                                </span>
                                <span className="trigger-name-display">{trigger.name || `Trigger ${idx + 1}`}</span>
                                <span className="expression-display">{trigger.expression || 'No schedule'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="triggers-footer">
                        <button
                          className="config-btn config-btn-secondary"
                          onClick={addCronTrigger}
                          disabled={cronTriggers.length >= 10}
                        >
                          <span className="btn-icon">➕</span>
                          Add Trigger Link
                        </button>
                        <button
                          className="config-btn config-btn-run-all"
                          onClick={runAllCronTriggers}
                          disabled={cronLoading || cronTriggers.length === 0}
                        >
                          <span className="btn-icon">{cronLoading ? '⏳' : '🚀'}</span>
                          {cronLoading ? 'Running All...' : 'RUN ALL TRIGGERS'}
                        </button>
                      </div>
                    </div>

                    {/* Scheduler Status Widget */}
                    <div className="scheduler-status-widget">
                      <div className="scheduler-header">
                        <h4>
                          <span className="scheduler-icon">🕐</span>
                          Refund Scheduler Status
                        </h4>
                      </div>
                      <div className="scheduler-content">
                        <div className="scheduler-status-info">
                          <div className="status-row">
                            <span className="status-label">Status:</span>
                            <span className={`status-value ${schedulerStatus === 'running' ? 'status-running' : schedulerStatus === 'stopped' ? 'status-stopped' : 'status-error'}`}>
                              {schedulerStatus}
                            </span>
                          </div>
                          <div className="status-row">
                            <span className="status-label">Next Run:</span>
                            <span className="status-value">{schedulerNextRun || 'Loading...'}</span>
                          </div>
                          <div className="status-row">
                            <span className="status-label">Timezone:</span>
                            <span className="status-value">{schedulerTimezone || 'Loading...'}</span>
                          </div>
                        </div>
                        <div className="scheduler-controls">
                          <button 
                            className="scheduler-btn scheduler-btn-start" 
                            onClick={startScheduler}
                            disabled={schedulerLoading || schedulerStatus === 'running'}
                          >
                            {schedulerLoading ? '⏳' : '▶️'} {schedulerLoading ? 'Starting...' : 'Start Scheduler'}
                          </button>
                          <button 
                            className="scheduler-btn scheduler-btn-stop" 
                            onClick={stopScheduler}
                            disabled={schedulerLoading || schedulerStatus === 'stopped'}
                          >
                            {schedulerLoading ? '⏳' : '⏹️'} {schedulerLoading ? 'Stopping...' : 'Stop Scheduler'}
                          </button>
                          <button 
                            className="scheduler-btn scheduler-btn-refresh" 
                            onClick={refreshSchedulerStatus}
                            disabled={schedulerLoading}
                          >
                            🔄 Refresh
                          </button>
                          <button 
                            className="scheduler-btn scheduler-btn-test" 
                            onClick={testSchedulerValues}
                            style={{backgroundColor: '#8b5cf6'}}
                          >
                            🧪 Test
                          </button>
                          <button 
                            className="scheduler-btn scheduler-btn-logs" 
                            onClick={openLogsModal}
                          >
                            📱 View Logs
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
                </div>
              )}

              {/* App Settings Modal */}
              {activeSettingsModal === 'app' && (
                <div className="modal-overlay" onClick={closeSettingsModal}>
                  <div className="modal-content settings-config-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close settings-modal-close" onClick={closeSettingsModal}>×</button>
                    <div className="cron-config-container settings-modal-panel">
                      <div className="cron-config-header">
                        <h3>
                          <span className="config-icon">📱</span>
                          App Settings
                        </h3>
                        <span className="cron-status-badge status-active">● MOBILE APP</span>
                      </div>

                      <div className="cron-config-body">
                        <div className="cron-main-settings">
                          <div className="config-form-group">
                            <label htmlFor="google-play-link">
                              <span className="label-icon">🛍️</span>
                              Google Play Store Link
                            </label>
                            <input
                              id="google-play-link"
                              type="url"
                              className="config-input"
                              value={appSettings.google_play_link}
                              onChange={(e) => setAppSettings((prev) => ({ ...prev, google_play_link: e.target.value }))}
                              placeholder="https://play.google.com/store/apps/details?id=..."
                            />
                          </div>

                          <div className="config-form-group">
                            <label htmlFor="direct-apk-link">
                              <span className="label-icon">🔗</span>
                              Direct APK Link
                            </label>
                            <input
                              id="direct-apk-link"
                              type="url"
                              className="config-input"
                              value={appSettings.direct_apk_link}
                              onChange={(e) => setAppSettings((prev) => ({ ...prev, direct_apk_link: e.target.value }))}
                              placeholder="https://your-domain.com/path/app.apk"
                            />
                          </div>

                          <div className="config-form-group">
                            <label htmlFor="apk-upload">
                              <span className="label-icon">📦</span>
                              Upload APK Version
                            </label>
                            <input
                              id="apk-upload"
                              type="file"
                              className="config-input"
                              accept=".apk"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
                                handleUploadApk(file);
                                e.currentTarget.value = '';
                              }}
                              disabled={appUploading}
                            />
                            <p className="backup-info muted">
                              Upload files like appv1.apk, appv2.apk. Every uploaded version will be listed below.
                            </p>
                          </div>

                          <div className="config-actions">
                            <button
                              className="config-btn config-btn-save"
                              onClick={saveAppSettings}
                              disabled={appSettingsLoading || appUploading}
                            >
                              <span className="btn-icon">{appSettingsLoading ? '⏳' : '💾'}</span>
                              {appSettingsLoading ? 'Saving...' : 'Save App Settings'}
                            </button>
                          </div>
                        </div>

                        <div className="cron-triggers-section">
                          <div className="triggers-header">
                            <h4>
                              <span className="triggers-icon">🧾</span>
                              Uploaded APK Versions
                              <span className="triggers-count">{appSettings.apk_versions.length}</span>
                            </h4>
                          </div>
                          <div className="triggers-list">
                            {appSettings.apk_versions.length === 0 ? (
                              <div className="backup-info">No APK versions uploaded yet.</div>
                            ) : (
                              appSettings.apk_versions.map((v, idx) => (
                                <div key={`${v.file_name}-${idx}`} className="trigger-item app-version-item">
                                  <div className="trigger-number">{idx + 1}</div>
                                  <div className="trigger-content app-version-content">
                                    <div className="trigger-status app-version-status">
                                      <span className="status-pill status-ready app-version-name" title={v.file_name}>{v.file_name}</span>
                                      <span className="trigger-name-display app-version-date">
                                        {v.uploaded_at ? new Date(v.uploaded_at).toLocaleString() : 'Uploaded'}
                                      </span>
                                      <span className="expression-display app-version-size">
                                        {v.size_bytes ? `${(v.size_bytes / (1024 * 1024)).toFixed(2)} MB` : ''}
                                      </span>
                                    </div>
                                    <div className="trigger-actions app-version-actions">
                                      <a
                                        className="trigger-btn trigger-btn-run"
                                        href={v.file_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Open APK file link"
                                      >
                                        Download
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings Modal */}
              {activeSettingsModal === 'security' && (
                <div className="modal-overlay" onClick={closeSettingsModal}>
                  <div className="modal-content settings-config-modal" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close settings-modal-close" onClick={closeSettingsModal}>×</button>
                    <div className="cron-config-container security-config-container settings-modal-panel">
                  <div className="cron-config-header">
                    <h3>
                      <span className="config-icon">🔐</span>
                      Security Settings
                    </h3>
                    <span className="cron-status-badge status-active">
                      ● {securitySettings.require_2fa ? '2FA REQUIRED' : 'STANDARD LOGIN'}
                    </span>
                  </div>

                  <div className="cron-config-body">
                    <div className="cron-main-settings">
                      <div className="cron-toggle-group">
                        <label className="cron-checkbox-label">
                          <input
                            type="checkbox"
                            className="cron-checkbox"
                            checked={securitySettings.otp_login_enabled}
                            onChange={(e) =>
                              setSecuritySettings((prev) => ({
                                ...prev,
                                otp_login_enabled: e.target.checked
                              }))
                            }
                          />
                          <span className="checkbox-text">
                            <span className="checkbox-icon">📲</span>
                            Enable OTP-based Login
                          </span>
                        </label>
                      </div>

                      <div className="cron-toggle-group">
                        <label className="cron-checkbox-label">
                          <input
                            type="checkbox"
                            className="cron-checkbox"
                            checked={securitySettings.require_2fa}
                            onChange={(e) =>
                              setSecuritySettings((prev) => ({
                                ...prev,
                                require_2fa: e.target.checked
                              }))
                            }
                          />
                          <span className="checkbox-text">
                            <span className="checkbox-icon">🔑</span>
                            Require 2FA after Login
                          </span>
                        </label>
                      </div>

                      <div className="cron-toggle-group">
                        <label className="cron-checkbox-label">
                          <input
                            type="checkbox"
                            className="cron-checkbox"
                            checked={securitySettings.require_otp_after_email_login}
                            onChange={(e) =>
                              setSecuritySettings((prev) => ({
                                ...prev,
                                require_otp_after_email_login: e.target.checked
                              }))
                            }
                          />
                          <span className="checkbox-text">
                            <span className="checkbox-icon">📧</span>
                            Require OTP after Email + Password Login
                          </span>
                        </label>
                      </div>

                      <div className="cron-toggle-group">
                        <label className="cron-checkbox-label">
                          <input
                            type="checkbox"
                            className="cron-checkbox"
                            checked={securitySettings.mobile_login_enabled}
                            onChange={(e) =>
                              setSecuritySettings((prev) => ({
                                ...prev,
                                mobile_login_enabled: e.target.checked
                              }))
                            }
                          />
                          <span className="checkbox-text">
                            <span className="checkbox-icon">📱</span>
                            Mobile based Login
                          </span>
                        </label>
                      </div>

                      <div className="cron-toggle-group">
                        <label className="cron-checkbox-label">
                          <input
                            type="checkbox"
                            className="cron-checkbox"
                            checked={securitySettings.email_login_enabled}
                            onChange={(e) =>
                              setSecuritySettings((prev) => ({
                                ...prev,
                                email_login_enabled: e.target.checked
                              }))
                            }
                          />
                          <span className="checkbox-text">
                            <span className="checkbox-icon">✉️</span>
                            Email based Login
                          </span>
                        </label>
                      </div>

                      <div className="cron-toggle-group">
                        <label className="cron-checkbox-label w-full">
                          <span className="checkbox-text w-full flex items-center justify-between gap-3">
                            <span className="flex items-center gap-2">
                              <span className="checkbox-icon">⏲️</span>
                              Auto Logout (minutes)
                            </span>
                            <input
                              type="number"
                              min={1}
                              max={240}
                              className="config-input w-28 text-right"
                              value={securitySettings.session_timeout_minutes}
                              onChange={(e) =>
                                setSecuritySettings((prev) => ({
                                  ...prev,
                                  session_timeout_minutes: Math.max(
                                    1,
                                    Math.min(240, parseInt(e.target.value || '0', 10) || 1)
                                  ),
                                }))
                              }
                            />
                          </span>
                        </label>
                      </div>

                      <div className="config-actions">
                        <button
                          className="config-btn config-btn-save"
                          onClick={saveSecuritySettings}
                          disabled={securityLoading}
                        >
                          <span className="btn-icon">{securityLoading ? '⏳' : '💾'}</span>
                          {securityLoading ? 'Saving...' : 'Save Security Settings'}
                        </button>
                      </div>

                      <div className="cron-help-text">
                        <span className="help-icon">💡</span>
                        Control whether OTP-based logins, 2FA, login methods (Mobile/Email), and automatic logout timeout are available for users.
                      </div>
                    </div>
                  </div>
                </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CRON Logs Modal */}
      {showLogsModal && (
        <div className="modal-overlay" onClick={closeLogsModal}>
          <div className="modal-content logs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 CRON Logs</h3>
              <button className="modal-close" onClick={closeLogsModal}>×</button>
            </div>
            <div className="modal-body">
              {logsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading logs...</p>
                </div>
              ) : logsData.length === 0 ? (
                <div className="no-data">
                  <p>📝 No logs found</p>
                  <button className="btn-secondary" onClick={handleRefreshLogs}>
                    🔄 Refresh
                  </button>
                </div>
              ) : (
                <div className="logs-container">
                  <div className="logs-header">
                    <span className="logs-count">
                      📊 {logsPagination.totalRecords} total entries (Page {logsPagination.currentPage} of {logsPagination.totalPages})
                      {logsAutoRefresh && <span className="live-indicator"> 🔴 LIVE</span>}
                    </span>
                    <div className="logs-controls">
                      <button 
                        className={`btn-toggle ${logsAutoRefresh ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}
                        onClick={toggleAutoRefresh}
                        title={logsAutoRefresh ? 'Stop auto-refresh' : 'Start auto-refresh (30s)'}
                      >
                        {logsAutoRefresh ? '⏸️ Stop' : '▶️ Live'}
                      </button>
                      <button className="btn-secondary" onClick={handleRefreshLogs}>
                        🔄 Refresh
                      </button>
                    </div>
                  </div>
                  <div className="logs-table-container">
                    <table className="logs-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Time</th>
                          <th>Merchant</th>
                          <th>Description</th>
                          <th>Transaction ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logsData.map((log, index) => (
                          <tr key={log.id || index} className="log-row">
                            <td className="log-id-cell">
                              <span className="log-id-badge">#{log.id}</span>
                            </td>
                            <td className="log-time-cell">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td className="log-merchant-cell">
                              {log.merchant_ref_no || 'N/A'}
                            </td>
                            <td className="log-description-cell">
                              {log.description}
                            </td>
                            <td className="log-transaction-cell">
                              {log.transaction_id || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {logsPagination.totalPages > 1 && (
                    <div className="logs-pagination">
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(logsPagination.currentPage - 1)}
                        disabled={logsPagination.currentPage <= 1 || logsLoading}
                      >
                        ← Previous
                      </button>
                      
                      <div className="pagination-info">
                        <span>Page {logsPagination.currentPage} of {logsPagination.totalPages}</span>
                      </div>
                      
                      <button 
                        className="pagination-btn"
                        onClick={() => handlePageChange(logsPagination.currentPage + 1)}
                        disabled={logsPagination.currentPage >= logsPagination.totalPages || logsLoading}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />
    </div>
  );
};

export default AdminDashboardNew;
